import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import api from '@src/api/clients';
import {
  approveBudgetCenter,
  copyFromMonth,
  discardBudgetCenterDraft,
  fetchAvailableMonths,
  fetchBudgetCenter,
  fetchBudgetComments,
  fetchBudgetCenterScopeSpend,
  cancelBudgetCenterTurnOff,
  markBudgetCenterLive,
  postBudgetComment,
  saveBudgetCenterDraft,
} from '@src/api/actions/dashboard/clientBudgets';
import {
  bust as bustBudgetCenterCache,
  peekBudgetCenter,
  peekScopeSpend,
} from '@src/api/actions/dashboard/budgetCenterCache';
import Page from '@src/components/layout/Page';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import useLocalStorage from '@src/hooks/useLocalStorage';
import useMultiClientBudgetCenter from '@src/hooks/useMultiClientBudgetCenter';
import useParentCompanyAutocomplete from '@src/hooks/useParentCompanyAutocomplete';
import {
  findScopeMixSegments,
  segmentLocationIds,
  segmentLocationKey,
  segmentVerticalId,
} from '@src/utils/budgetScope';
import { useClientView } from '@src/components/layout/AppLayout/ClientViewSwitcher';
import ActivePacingHoldsBanner from './components/ActivePacingHoldsBanner';

function formatCurrency(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '$0.00';
  }
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatNumberInput(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const raw = String(value).replace(/,/g, '');
  if (!/^[-]?\d*\.?\d*$/.test(raw)) {
    return String(value);
  }
  const [whole, fraction] = raw.split('.');
  const normalizedWhole = whole === '' || whole === '-' ? whole : Number(whole).toLocaleString('en-US');
  if (fraction !== undefined) {
    return `${normalizedWhole}.${fraction}`;
  }
  return normalizedWhole;
}

function getErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) {
    return fallback;
  }
  if (typeof data === 'string') {
    return data;
  }
  if (data.detail) {
    return data.detail;
  }
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join(', ');
}

function getCurrentMonthStart() {
  // Local time, matching the isCurrentMonth/isPastMonth dayjs() checks below —
  // a UTC month here made late-evening US users land on next month (and
  // UTC+10 users on the "past" month) at month boundaries.
  return dayjs().startOf('month').format('YYYY-MM-DD');
}

function getTodayDateString() {
  return dayjs().format('YYYY-MM-DD');
}

// Default adjustment date clamped into the budget month — "today" is outside
// the allowed range when adjusting a future month's budget.
function getDefaultAdjustmentDate(period) {
  const today = dayjs();
  if (!period || today.isSame(dayjs(period), 'month')) {
    return today.format('YYYY-MM-DD');
  }
  return dayjs(period).startOf('month').format('YYYY-MM-DD');
}

function formatMonthLabel(value) {
  if (!value) {
    return 'Current Month';
  }
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatActorLabel(event) {
  if (!event) {
    return '';
  }
  return event?.actor?.name || event?.actor?.email || 'Unknown user';
}

function formatHistoryValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'Empty';
  }
  if (typeof value === 'object') {
    if (value.name) {
      return value.name;
    }
    if (Array.isArray(value)) {
      return value.join(', ') || 'Empty';
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function formatStatusLabel(value, options = {}) {
  const { planned = false } = options;
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'off') {
    return 'Off';
  }
  if (normalized === 'pending_turn_off') {
    return 'Turning Off';
  }
  if (normalized === 'spent_out') {
    return 'Budget Spent';
  }
  if (normalized === 'scheduled') {
    return 'Scheduled';
  }
  if (normalized === 'active') {
    return 'Live';
  }
  if (normalized === 'approved') {
    return 'Approved';
  }
  if (normalized === 'pending_approval') {
    return 'Submitted';
  }
  if (normalized === 'draft') {
    return planned ? 'Planned' : 'Draft';
  }
  if (normalized === 'completed' || normalized === 'archived') {
    return 'Completed';
  }
  return normalized ? normalized.replaceAll('_', ' ') : 'Draft';
}

function getStatusChipColor(value) {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'off') {
    return 'error';
  }
  if (normalized === 'pending_turn_off' || normalized === 'spent_out') {
    return 'warning';
  }
  if (normalized === 'scheduled') {
    return 'info';
  }
  if (normalized === 'active') {
    return 'success';
  }
  if (normalized === 'approved' || normalized === 'pending_approval') {
    return 'warning';
  }
  if (normalized === 'completed' || normalized === 'archived') {
    return 'default';
  }
  return 'info';
}

function getPacingColor(pct) {
  if (pct < 90) return '#3b82f6';
  if (pct < 110) return '#22c55e';
  if (pct < 120) return '#f59e0b';
  return '#ef4444';
}

function getPacingPillSx(pct) {
  if (pct < 90) return { color: 'info.dark', bgcolor: 'rgba(59,130,246,0.15)', borderColor: 'rgba(59,130,246,0.45)' };
  if (pct < 110) return { color: 'success.dark', bgcolor: 'rgba(34,197,94,0.15)', borderColor: 'rgba(34,197,94,0.5)' };
  if (pct < 120) return { color: 'warning.dark', bgcolor: 'rgba(245,158,11,0.2)', borderColor: 'rgba(245,158,11,0.5)' };
  return { color: 'error.dark', bgcolor: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.45)' };
}

// Scheduled market launch. A segment's starts_on holds its money until that
// date: before it the effective budget is $0, which is neither "off" nor
// under-pacing, and on the day itself pacing still reads yesterday's
// pre-launch $0. A start on the 1st is just the month start. `pacingNote` is
// set only for a funded segment whose pacing has nothing to measure yet.
export function getSegmentLaunch(segment, todayIso = getTodayDateString()) {
  const startsOn = segment?.starts_on;
  if (!startsOn || dayjs(startsOn).date() === 1) {
    return null;
  }
  const label = dayjs(startsOn).format('MMM D');
  const funded = Number(segment?.amount || 0) > 0;
  if (startsOn > todayIso) {
    return {
      phase: 'scheduled',
      caption: `Starts ${label} — nothing spends before this date`,
      pacingNote: funded ? `Launches ${label}` : null,
    };
  }
  if (startsOn === todayIso) {
    return {
      phase: 'launch_day',
      caption: 'Started today — pacing shows from tomorrow',
      pacingNote: funded ? 'Launched today' : null,
    };
  }
  return { phase: 'launched', caption: `Started ${label}`, pacingNote: null };
}

function PacingCell({ effectiveAmount, monthToDateSpend, pacingPercentage, isTurnedOff, launchNote }) {
  const budget = Number(effectiveAmount || 0);
  const spend = Number(monthToDateSpend || 0);
  // A turned-off segment isn't pacing toward anything — its budget was settled
  // down to what it already spent, so a percentage there is noise at best and
  // an alarming "135%" at worst. The row's Off chip carries the state instead.
  if (isTurnedOff) {
    return (
      <Typography variant="body2" color="text.secondary" title="Segment is off — no longer pacing">
        —
      </Typography>
    );
  }
  if (launchNote) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {launchNote}
      </Typography>
    );
  }
  if (budget <= 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const backendPct = pacingPercentage === null || pacingPercentage === undefined || pacingPercentage === ''
    ? null
    : Number(pacingPercentage);
  const pct = backendPct !== null && Number.isFinite(backendPct)
    ? Math.round(backendPct)
    : Math.round((spend / budget) * 100);
  const barPct = Math.min(pct, 200);
  const barColor = getPacingColor(pct);
  const pillSx = getPacingPillSx(pct);

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: '0.8rem',
          px: 1,
          py: 0.25,
          borderRadius: 2,
          border: '1px solid',
          minWidth: 56,
          ...pillSx,
        }}
      >
        {pct}%
      </Box>
      <Box sx={{ flex: 1, minWidth: 40, height: 6, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(barPct, 100)}%`, height: '100%', borderRadius: 1, bgcolor: barColor, transition: 'width 300ms ease' }} />
      </Box>
    </Stack>
  );
}

function SummaryCard({ label, value, helper }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Stack spacing={1}>
          <Typography color="text.secondary" variant="overline">
            {label}
          </Typography>
          <Typography variant="h5">{value}</Typography>
          <Typography color="text.secondary" variant="body2">
            {helper}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SummaryMetric({ label, value, helper }) {
  return (
    <Stack minWidth={0} spacing={0.5}>
      <Typography color="text.secondary" variant="overline">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
      {helper ? (
        <Typography color="text.secondary" variant="caption">
          {helper}
        </Typography>
      ) : null}
    </Stack>
  );
}

function MonthNavigator({ value, onChange, isStaff }) {
  const parsed = dayjs(value || getCurrentMonthStart());
  const isThisMonth = parsed.isSame(dayjs(), 'month');
  const isNextMonth = parsed.isSame(dayjs().add(1, 'month'), 'month');
  const isPast = parsed.isBefore(dayjs(), 'month');

  const prev = () => onChange(parsed.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'));
  const next = () => onChange(parsed.add(1, 'month').startOf('month').format('YYYY-MM-DD'));

  return (
    <Stack alignItems="center" direction="row" spacing={0.5}>
      <Tooltip title="Previous month">
        <span>
          <IconButton onClick={prev} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Box sx={{ minWidth: 148, textAlign: 'center' }}>
        <Typography fontWeight={600} variant="body1">
          {parsed.format('MMMM YYYY')}
        </Typography>
        {isThisMonth ? (
          <Chip color="success" label="This Month" size="small" variant="outlined" />
        ) : isNextMonth ? (
          <Chip color="info" label="Next Month" size="small" variant="outlined" />
        ) : isPast ? (
          <Chip label="Past" size="small" variant="outlined" />
        ) : null}
      </Box>
      <IconButton onClick={next} size="small">
        <ChevronRightIcon />
      </IconButton>
    </Stack>
  );
}

const WORKFLOW_STEPS_CLIENT = ['Set Budget', 'Submitted', 'Approved', 'Live'];
const WORKFLOW_STEPS_STAFF = ['Draft', 'Submitted', 'Approved', 'Live'];

function getWorkflowActiveStep(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 3;
  if (s === 'approved') return 2;
  if (s === 'pending_approval') return 1;
  return 0;
}

function WorkflowStepper({ status, isStaff }) {
  const steps = isStaff ? WORKFLOW_STEPS_STAFF : WORKFLOW_STEPS_CLIENT;
  const activeStep = getWorkflowActiveStep(status);
  return (
    <Stepper activeStep={activeStep} alternativeLabel sx={{ width: '100%', pt: 0.5 }}>
      {steps.map((label) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}

// Verticals a territory actually runs, read off the per-vertical active ZIP
// counts the territory endpoint returns. `_all` includes filtered-out ZIPs — a
// market whose ZIPs are all filtered out still belongs to the vertical.
function territoryVerticalIds(row) {
  const counts = {
    ...(row?.vertical_zip_counts || {}),
    ...(row?.vertical_zip_counts_all || {}),
  };
  return Object.entries(counts)
    .filter(([, count]) => Number(count) > 0)
    .map(([verticalId]) => Number(verticalId))
    .filter((id) => Number.isFinite(id) && id > 0);
}

// Location options for one segment row: only the markets that run that row's
// vertical. "All verticals" keeps the full list, and so does a client whose
// territories carry no per-vertical counts (older payloads) — the picker never
// hides a market it can't classify. Already-picked markets always stay visible
// so an existing segment can't silently lose its own territory chips.
function scopedLocationOptions(options, verticalId, selectedIds = []) {
  const list = options || [];
  if (!verticalId) {
    return list;
  }
  const selected = new Set((selectedIds || []).map(Number));
  const scoped = list.filter((option) => {
    const ids = option?.verticalIds;
    if (!ids || !ids.length) return true;
    return ids.includes(Number(verticalId)) || selected.has(Number(option.value));
  });
  return scoped.length ? scoped : list;
}

function emptySegment(period) {
  return {
    budget_id: null,
    source_budget_id: null,
    vertical: '',
    location: '',
    channel: '',
    amount: '',
    starts_on: '',
    notes: '',
    adjustment_total: '0.00',
    effective_amount: '',
    workflow_status: 'draft',
    adjustments: [],
    vertical_data: null,
    location_data: null,
    channel_data: null,
    pendingAdjustment: {
      amount_delta: '',
      effective_date: getDefaultAdjustmentDate(period),
      notes: '',
    },
  };
}

// Plain-English causes for the staff "No ZIPs targeted" chip and banner.
const TARGETING_GAP_CAUSE = {
  paused: 'its ZIP targeting is paused',
  deactivated: 'its ZIPs were removed but the budget carried forward',
  filtered: 'every ZIP is excluded by the ZIP filters',
  no_rows: 'no ZIPs were ever added for this vertical here',
};

function normalizeSegment(segment, period) {
  return {
    budget_id: segment?.budget_id ?? segment?.id ?? null,
    source_budget_id: segment?.source_budget_id ?? segment?.id ?? null,
    vertical: segmentVerticalId(segment),
    location: segment?.location ?? segment?.location_data?.id ?? segmentLocationIds(segment)[0] ?? '',
    channel: segment?.channel ?? segment?.channel_data?.id ?? '',
    amount: segment?.amount ?? '',
    starts_on: segment?.starts_on ?? '',
    notes: segment?.notes ?? '',
    adjustment_total: segment?.adjustment_total ?? '0.00',
    effective_amount: segment?.effective_amount ?? segment?.amount ?? '',
    location_ids: segmentLocationIds(segment),
    month_to_date_spend: segment?.month_to_date_spend ?? 0,
    projected_spend: segment?.projected_spend ?? 0,
    remaining_per_day: segment?.remaining_per_day ?? 0,
    pacing_percentage: segment?.pacing_percentage ?? null,
    workflow_status: segment?.workflow_status ?? 'draft',
    has_pending_turn_off: Boolean(segment?.has_pending_turn_off),
    // Carried through so the row chip can count the hold window down —
    // this normalizer is a whitelist, so an omitted field silently degrades
    // the chip to its no-deadline label.
    auto_applies_at: segment?.auto_applies_at ?? null,
    // Staff-only overlay from the API: this funded segment has no targetable
    // ZIPs (paused / removed / never built). Null for clients and healthy rows.
    targeting_gap: segment?.targeting_gap ?? null,
    is_turned_off: Boolean(segment?.is_turned_off),
    adjustments: Array.isArray(segment?.adjustments) ? segment.adjustments : [],
    vertical_data: segment?.vertical_data ?? null,
    location_data: segment?.location_data ?? null,
    channel_data: segment?.channel_data ?? null,
    pendingAdjustment: {
      amount_delta: '',
      effective_date: getDefaultAdjustmentDate(period),
      notes: '',
    },
  };
}

function segmentSignature(segment) {
  return [
    segment.vertical || '',
    segment.location || '',
    segment.channel || '',
    String(segment.amount || ''),
    String(segment.notes || ''),
  ].join('|');
}

function cloneSegment(segment) {
  return {
    ...segment,
    budget_id: null,
    source_budget_id: null,
    workflow_status: 'draft',
    adjustment_total: '0.00',
    effective_amount: segment?.amount ?? '',
    month_to_date_spend: '0.00',
    projected_spend: '0.00',
    remaining_per_day: '0.00',
    pacing_percentage: null,
    adjustments: [],
    pendingAdjustment: {
      amount_delta: '',
      effective_date: getTodayDateString(),
      notes: '',
    },
  };
}

function computeClientHasDraftChanges(clientData, period) {
  if (!clientData) return false;
  if (clientData.pageData?.has_unsubmitted_draft) return true;
  const loaded = Array.isArray(clientData.pageData?.segments) ? clientData.pageData.segments : [];
  const current = Array.isArray(clientData.segments) ? clientData.segments : [];
  if (loaded.length !== current.length) return true;
  const currentKey = JSON.stringify(current.map(segmentDraftComparable));
  const loadedKey = JSON.stringify(
    loaded.map((s) => segmentDraftComparable(normalizeSegment(s, clientData.pageData?.period || period)))
  );
  return currentKey !== loadedKey;
}

function segmentDraftComparable(segment) {
  return {
    budget_id: segment?.budget_id ?? null,
    source_budget_id: segment?.source_budget_id ?? null,
    vertical: segment?.vertical || '',
    location: segment?.location || '',
    // Sorted so [A,B] vs [B,A] (same set, different pick order) isn't "dirty",
    // but adding/removing any territory IS — without this, multi-territory
    // edits were invisible to dirty-checking and could never be saved.
    location_ids: segmentLocationIds(segment).sort((a, b) => a - b),
    channel: segment?.channel || '',
    amount: String(segment?.amount ?? ''),
    starts_on: segment?.starts_on || '',
    notes: segment?.notes || '',
    adjustments: Array.isArray(segment?.adjustments)
      ? segment.adjustments.map((adjustment) => ({
        id: adjustment?.id ?? null,
        amount_delta: String(adjustment?.amount_delta ?? ''),
        effective_date: adjustment?.effective_date || '',
        notes: adjustment?.notes || '',
      }))
      : [],
  };
}

// Stable display order for segment rows: vertical label, then territory
// label, then budget id. The API returns rows in creation order, which reads
// as a scrambled wall for multi-segment clients. Labels come from the rows'
// own vertical_data/location_data (present on raw payload rows AND preserved
// by normalizeSegment), so the sort never depends on the separately-fetched
// option lists — no race on first load. Ids break ties so the order is fully
// deterministic across shapes.
function segmentDisplaySortKey(segment) {
  const territoryIds = segmentLocationKey(segment);
  return {
    verticalLabel: segment?.vertical_data?.vertical || '',
    verticalId: Number(segmentVerticalId(segment)) || 0,
    territoryLabel: segment?.location_data?.name || '',
    territoryIds,
    budgetId: Number(segment?.budget_id ?? segment?.id ?? 0) || 0,
  };
}

function compareSegmentsForDisplay(a, b) {
  const keyA = segmentDisplaySortKey(a);
  const keyB = segmentDisplaySortKey(b);
  return keyA.verticalLabel.localeCompare(keyB.verticalLabel)
    // Vertical id keeps rows of the same vertical contiguous even when labels
    // are missing/equal — the table's group headers rely on contiguity.
    || (keyA.verticalId - keyB.verticalId)
    || keyA.territoryLabel.localeCompare(keyB.territoryLabel)
    || keyA.territoryIds.localeCompare(keyB.territoryIds)
    || (keyA.budgetId - keyB.budgetId);
}

function sortSegmentRows(rows) {
  return [...rows].sort(compareSegmentsForDisplay);
}

// Sort a server payload's segments ONCE before it lands in state. pageData
// .segments and the editable rows are index-compared by the dirty checks
// (hasLocalSegmentEdits, segmentsUnchangedFromLoad) and the cache-revalidate
// path, so the sorted payload must be the single source both derive from —
// sorting only the editable copy would make the page read permanently dirty.
// Never re-sort reactively during editing: rows must not jump mid-edit.
function sortPayloadSegments(payload) {
  if (!payload || !Array.isArray(payload.segments) || payload.segments.length < 2) {
    return payload;
  }
  return { ...payload, segments: sortSegmentRows(payload.segments) };
}

function buildSegmentLabel(segment, options) {
  const verticalLabel = options.verticals.find((row) => String(row.value) === String(segment.vertical || ''))?.label;
  const locationLabel = options.locations.find((row) => String(row.value) === String(segment.location || ''))?.label;
  return [verticalLabel, locationLabel].filter(Boolean).join(' / ') || 'Segment';
}

function isExistingSegment(segment) {
  return Boolean(segment?.budget_id);
}

function isLiveOriginSegment(segment, liveBudgetIds) {
  const sourceBudgetId = segment?.source_budget_id;
  if (!sourceBudgetId) {
    return false;
  }
  return Array.isArray(liveBudgetIds) && liveBudgetIds.includes(sourceBudgetId);
}

// Chip copy for a segment inside the turn-off hold window. Reads the
// segment's own server-supplied deadline (auto_applies_at) so the 15 minutes
// is never hardcoded here, and falls back to a plain label when auto-approval
// is off — those rows really are waiting on a human.
// Re-renders (and so re-ticks) on the page's 1s countdown state.
// Deadlines whose post-expiry refetch has already fired, at module scope so it
// survives the page remount that the refetch itself triggers. See the effect
// that consumes it.
const settledTurnOffDeadlines = new Set();

function turnOffChipLabel(segment) {
  const deadline = segment?.auto_applies_at;
  if (!deadline) return 'Turning off';
  const seconds = Math.max(0, Math.ceil((dayjs(deadline).valueOf() - Date.now()) / 1000));
  if (seconds <= 0) return 'Turning off';
  return `Turning off in ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function getSegmentOperationalStatus(segment) {
  // Pending turn-off: submitted, inside the ~15-minute hold window before it
  // auto-applies (still spending until then)
  if (segment?.has_pending_turn_off) {
    return 'pending_turn_off';
  }

  // Turn-off executed: the segment is off
  if (segment?.is_turned_off) {
    return 'off';
  }

  // Funded launch that hasn't started: its $0 effective budget isn't a
  // turn-off. A draft or submitted launch keeps saying so — "Scheduled" would
  // hide that nobody has approved it.
  if (getSegmentLaunch(segment)?.phase === 'scheduled' && Number(segment?.amount || 0) > 0) {
    const workflowStatus = String(segment?.workflow_status || 'draft').toLowerCase();
    return ['draft', 'pending_approval'].includes(workflowStatus) ? workflowStatus : 'scheduled';
  }

  // If the amount hasn't been entered yet (new row being filled in), treat as draft — not "off"
  const rawAmount = segment?.effective_amount ?? segment?.amount;
  if (rawAmount === '' || rawAmount === null || rawAmount === undefined) {
    return segment?.workflow_status || 'draft';
  }

  const effectiveAmount = Number(rawAmount);
  const monthToDateSpend = Number(segment?.month_to_date_spend ?? 0);

  // A zero effective budget with no explicit turn-off flag still reads as off.
  if (effectiveAmount <= 0) {
    return 'off';
  }
  // Spend meeting the budget is NOT a turn-off (the flags above cover real
  // turn-offs) — it's a segment that organically spent out, e.g. under the
  // budget-cap auto-pause. Showing it as "Off" scared clients who never
  // turned anything off.
  if (monthToDateSpend > 0 && effectiveAmount - monthToDateSpend < 0.005) {
    return 'spent_out';
  }

  return segment?.workflow_status || 'draft';
}

export function getBudgetCenterRowStatus(segment, { isLiveMonth, liveBudgetIds }) {
  const operationalStatus = String(getSegmentOperationalStatus(segment) || '').toLowerCase();
  if (operationalStatus === 'off') {
    return 'off';
  }
  if (operationalStatus === 'pending_turn_off') {
    return 'pending_turn_off';
  }
  if (operationalStatus === 'spent_out') {
    return 'spent_out';
  }
  if (operationalStatus === 'scheduled') {
    return 'scheduled';
  }
  if (isLiveMonth && isLiveOriginSegment(segment, liveBudgetIds)) {
    return 'active';
  }
  return segment?.workflow_status || 'draft';
}

const WALKTHROUGH_STEPS_STAFF = [
  {
    ref: 'clientSelector',
    title: 'Select a Client',
    description: 'Pick the client whose budget you want to manage.',
  },
  {
    ref: 'monthNavigator',
    title: 'Navigate Months',
    description: 'Use the arrows to switch between months. Set future month budgets, or view and adjust the current live month.',
  },
  {
    ref: 'budgetTable',
    title: 'Budget Segments',
    description: 'Each row is a budget segment with vertical and location targeting. For live months you\'ll see MTD spend, pacing, and remaining budget. Use Add Adjustment for in-month changes.',
  },
  {
    ref: 'actionBar',
    title: 'Approve or Mark Live',
    description: 'Approve submitted budgets or mark approved budgets live. You can also save drafts and discard changes.',
  },
  {
    ref: 'discussion',
    title: 'Discussion',
    description: 'Leave comments for the client or internal notes about budget changes.',
  },
];

const WALKTHROUGH_STEPS_CLIENT = [
  {
    ref: 'monthNavigator',
    title: 'Navigate Months',
    description: 'Use the arrows to view past months or set your budget for the upcoming month.',
  },
  {
    ref: 'budgetTable',
    title: 'Your Budget Segments',
    description: 'Each row is a budget segment. Set your monthly amount per vertical and location. For the current month you\'ll see your spend, pacing, and remaining budget.',
  },
  {
    ref: 'actionBar',
    title: 'Submit for Review',
    description: 'When your budget looks good, submit it for Lead Gurus to review. You can also save a draft to come back later.',
  },
  {
    ref: 'discussion',
    title: 'Discussion',
    description: 'Ask questions or discuss budget changes with Lead Gurus here.',
  },
];

const WALKTHROUGH_STEPS_MULTI_CLIENT = [
  {
    ref: 'mcMonthNavigator',
    title: 'Pick a Month',
    description: 'Use the arrows to set budgets for next month or review past months. All clients under your parent company show in one view.',
  },
  {
    ref: 'mcBudgetTable',
    title: 'All Your Clients',
    description: 'One row per client. Edit amounts, verticals, and locations inline — or click the Open icon on any row for full details, history, and discussion.',
  },
  {
    ref: 'mcSaveProgress',
    title: 'Save Everyone At Once',
    description: 'Save Progress saves every client that has in-progress edits. Clients already submitted or live are left untouched.',
  },
  {
    ref: 'mcBudgetTable',
    title: 'Submit Per Client',
    description: 'When a client\'s budget is ready, use Submit on that row. Each client is reviewed independently — you can submit some while still working on others.',
  },
];

function MobileSegmentCard({
  segment, index, rowErrors, isLiveMonth, isCurrentMonth, liveOrigin, showAdjustmentColumns,
  verticalOptions, locationOptions, updateSegment, onNotes, onDuplicate,
  onAdjustment, onTurnOff, onTurnOn, isStaff, onDelete, canDelete, rowStatus, isFutureMonth, optionSet,
  previousAmount, isReadOnly,
}) {
  const launch = getSegmentLaunch(segment);
  const isScheduledLaunch = Boolean(launch?.pacingNote) && launch.phase === 'scheduled';
  const remaining = Number(segment.effective_amount || segment.amount || 0) - Number(segment.month_to_date_spend || 0);
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Chip
              color={getStatusChipColor(rowStatus)}
              label={formatStatusLabel(rowStatus, { planned: isFutureMonth && String(rowStatus || '').toLowerCase() === 'draft' })}
              size="small"
              variant="filled"
            />
            {!segment.vertical && !segment.location ? (
              <Chip label="Flexible" size="small" variant="soft" />
            ) : null}
          </Stack>

          <Stack direction="row" spacing={1}>
            <TextField
              disabled={liveOrigin || isReadOnly}
              error={Boolean(rowErrors?.segment)}
              fullWidth
              select
              size="small"
              label="Vertical"
              value={segment.vertical}
              onChange={(e) => updateSegment(index, 'vertical', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {verticalOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <Autocomplete
              multiple
              size="small"
              disabled={liveOrigin || isReadOnly}
              options={scopedLocationOptions(
                locationOptions,
                segment.vertical,
                segmentLocationIds(segment),
              )}
              getOptionLabel={(opt) => opt.label || ''}
              value={locationOptions.filter((o) => segmentLocationIds(segment).includes(o.value))}
              onChange={(_, val) => {
                const ids = (val || []).map((item) => item.value);
                updateSegment(index, 'location_ids', ids);
                updateSegment(index, 'location', ids[0] || '');
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => <TextField {...params} label="Location" placeholder={segmentLocationIds(segment).length ? '' : 'All'} />}
            />
          </Stack>

          {rowErrors?.segment ? (
            <Typography color="error" variant="caption">{rowErrors.segment}</Typography>
          ) : null}

          <TextField
            disabled={liveOrigin || isReadOnly}
            error={Boolean(rowErrors?.amount)}
            fullWidth
            helperText={rowErrors?.amount || ''}
            size="small"
            label="Starting Budget"
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            value={formatNumberInput(segment.amount)}
            onChange={(e) => updateSegment(index, 'amount', e.target.value.replace(/,/g, ''))}
          />
          {(segment.starts_on || !liveOrigin) && !isReadOnly ? (
            <TextField
              disabled={liveOrigin || isReadOnly}
              fullWidth
              size="small"
              type="date"
              label="Starts on (optional)"
              helperText={launch?.caption || (segment.starts_on ? 'Nothing spends before this date.' : 'Leave blank to start on the 1st.')}
              value={segment.starts_on || ''}
              onChange={(e) => updateSegment(index, 'starts_on', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          ) : launch ? (
            <Typography variant="caption" color="text.secondary">{launch.caption}</Typography>
          ) : null}
          {isCurrentMonth && !showAdjustmentColumns
            && Number(segment.month_to_date_spend || 0) > 0
            && !rowErrors?.amount ? (
              <Typography variant="caption" color="text.secondary">
                min {formatCurrency(segment.month_to_date_spend)} — already spent this month
              </Typography>
            ) : null}
          {(() => {
            if (previousAmount === null || previousAmount === 0) return null;
            const delta = Number(segment.amount || 0) - previousAmount;
            if (delta === 0) return (
              <Typography variant="caption" color="text.secondary">Same as last month</Typography>
            );
            return (
              <Typography variant="caption" sx={{ color: delta > 0 ? 'success.main' : 'error.main' }}>
                was {formatCurrency(previousAmount)} last month
              </Typography>
            );
          })()}

          {showAdjustmentColumns ? (
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Adjustments</Typography>
                <Typography variant="body2">{formatCurrency(segment.adjustment_total)}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Effective</Typography>
                <Typography variant="body2">{formatCurrency(segment.effective_amount || segment.amount)}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">MTD Spend</Typography>
                <Typography variant="body2">{formatCurrency(segment.month_to_date_spend)}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Remaining</Typography>
                <Typography
                  variant="body2"
                  sx={remaining < 0 && !isScheduledLaunch
                    ? { color: 'error.main', fontWeight: 600 }
                    : undefined}
                >
                  {formatCurrency(remaining)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Remaining/Day</Typography>
                <Typography variant="body2">
                  {segment.is_turned_off || isScheduledLaunch ? '—' : formatCurrency(segment.remaining_per_day)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Pacing</Typography>
                <PacingCell effectiveAmount={segment.effective_amount || segment.amount} monthToDateSpend={segment.month_to_date_spend} pacingPercentage={segment.pacing_percentage} isTurnedOff={segment.is_turned_off} launchNote={launch?.pacingNote} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Projected</Typography>
                <Typography variant="body2">{launch?.pacingNote ? '—' : formatCurrency(segment.projected_spend)}</Typography>
              </Box>
            </Stack>
          ) : null}

          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Button size="small" onClick={() => onNotes(index)}>
              {segment.notes ? 'Edit Notes' : 'Notes'}
            </Button>
            <Button size="small" onClick={() => onDuplicate(index)}>Duplicate</Button>
            {showAdjustmentColumns ? (
              <Button
                color="primary"
                size="small"
                startIcon={<EditOutlinedIcon />}
                variant="contained"
                onClick={() => onAdjustment(index)}
              >
                Adjustment
              </Button>
            ) : null}
            {isStaff && segment.targeting_gap ? (
              <Tooltip
                title={`Budget is live but this market has no targetable ZIPs — ${TARGETING_GAP_CAUSE[segment.targeting_gap.cause] || segment.targeting_gap.cause}. Nothing will spend here until the ZIPs are resumed/added or the segment is turned off.`}
              >
                <Chip
                  icon={<WarningAmberOutlinedIcon fontSize="small" />}
                  label="No ZIPs targeted"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
            ) : null}
            {isLiveMonth ? (
              isStaff && segment.is_turned_off ? (
                <Button color="success" size="small" onClick={() => onTurnOn(segment)}>Turn Back On</Button>
              ) : segment.has_pending_turn_off && !isStaff ? (
                <Chip label={turnOffChipLabel(segment)} size="small" color="warning" variant="outlined" />
              ) : segment.budget_id && !segment.is_turned_off ? (
                <Button size="small" onClick={() => onTurnOff(segment)}>
                  Turn Off
                </Button>
              ) : null
            ) : null}
            <IconButton
              size="small"
              onClick={() => onDelete(index)}
              disabled={!canDelete || liveOrigin}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Simplified read-friendly budget view for the ~82% of clients running 1-2
// segments on the live month. Surfaces the budget, spend, pacing health, and a
// single "Request a change" action instead of the full editing table.
function SimpleBudgetCard({ segment, period, verticalOptions, locationOptions, onRequestChange, onTurnOff }) {
  const operationalStatus = getSegmentOperationalStatus(segment);
  const isOff = operationalStatus === 'off';
  const isPendingOff = operationalStatus === 'pending_turn_off';
  const isSpentOut = operationalStatus === 'spent_out';
  // Before or on a funded launch date there is nothing to pace yet. Before it
  // the effective budget is $0, so the card shows the amount that launches.
  const launch = getSegmentLaunch(segment);
  const holdPacing = Boolean(launch?.pacingNote) && !isOff && !isPendingOff;
  const budget = holdPacing && launch.phase === 'scheduled'
    ? Number(segment.amount || 0)
    : Number(segment.effective_amount || segment.amount || 0);
  const spent = Number(segment.month_to_date_spend || 0);
  const remaining = budget - spent;
  const rawPacing = segment.pacing_percentage;
  const pacing = rawPacing === null || rawPacing === undefined || rawPacing === ''
    ? (budget > 0 ? (spent / budget) * 100 : 0)
    : Number(rawPacing);
  const pct = Math.round(pacing);

  const daysLeft = (() => {
    const [y, m] = String(period || '').split('-').map(Number);
    if (!y || !m) return null;
    const now = new Date();
    if (now.getFullYear() !== y || now.getMonth() + 1 !== m) return null;
    const end = new Date(y, m, 0).getDate();
    return Math.max(0, end - now.getDate());
  })();

  // Pacing health isn't meaningful early in the month or before any spend — raw
  // spend÷budget makes everyone look "under-pacing" on day 3. Show a neutral
  // "Just getting started" until enough of the month has elapsed and there's spend.
  const monthProgress = (() => {
    const [y, m] = String(period || '').split('-').map(Number);
    if (!y || !m) return null;
    const now = new Date();
    if (now.getFullYear() !== y || now.getMonth() + 1 !== m) return null;
    const end = new Date(y, m, 0).getDate();
    return now.getDate() / end;
  })();
  const tooEarlyToPace = (monthProgress !== null && monthProgress < 0.2)
    || (spent === 0 && (monthProgress === null || monthProgress < 0.5));
  // Off / pending-off / spent-out override pacing health — the card must not
  // claim "On track" for a segment that isn't actually running.
  const health = isOff
    ? 'Off'
    : isPendingOff
      ? 'Turning off'
      : isSpentOut
        ? 'Budget spent'
        : holdPacing
          ? launch.pacingNote
          : tooEarlyToPace
            ? 'Just getting started'
            : pct < 90 ? 'Under-pacing'
              : pct < 110 ? 'On track'
                : pct < 120 ? 'Running hot'
                  : 'Over-pacing';
  const barColor = isOff
    ? '#9ca3af'
    : (isPendingOff || isSpentOut)
      ? '#f59e0b'
      : (tooEarlyToPace || holdPacing) ? '#9ca3af' : getPacingColor(pct);

  const vertLabel = segment.vertical
    ? (verticalOptions.find((o) => String(o.value) === String(segment.vertical))?.label || null)
    : null;
  const locIds = segmentLocationIds(segment);
  const locLabel = locIds.length
    ? locIds.map((id) => locationOptions.find((o) => o.value === id)?.label).filter(Boolean).join(', ')
    : null;
  const scope = [vertLabel, locLabel].filter(Boolean).join(' · ');
  const fillPct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              {scope || 'Your monthly budget'}
            </Typography>
            <Chip
              size="small"
              label={health}
              variant="outlined"
              sx={{ color: barColor, borderColor: barColor, fontWeight: 600 }}
            />
          </Stack>

          <Box>
            <Typography variant="overline" color="text.secondary">Monthly budget</Typography>
            <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1.1 }}>
              {formatCurrency(budget)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Spent so far</Typography>
              <Typography variant="h6">{formatCurrency(spent)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Remaining</Typography>
              <Typography variant="h6" sx={remaining < 0 ? { color: 'error.main' } : undefined}>
                {formatCurrency(remaining)}
              </Typography>
            </Box>
            {daysLeft !== null ? (
              <Box>
                <Typography variant="caption" color="text.secondary">Days left</Typography>
                <Typography variant="h6">{daysLeft}</Typography>
              </Box>
            ) : null}
          </Stack>

          <Box>
            <Box sx={{ height: 8, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
              <Box sx={{ width: `${fillPct}%`, height: '100%', bgcolor: barColor, transition: 'width 300ms ease' }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {isOff
                ? 'This segment is turned off — no new spend.'
                : holdPacing ? `${launch.caption}.` : `${pct}% paced`}
            </Typography>
          </Box>

          {isPendingOff ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {segment?.auto_applies_at
                ? `This segment is ${turnOffChipLabel(segment).toLowerCase()}. Spend continues until then — use Undo at the top of the page to keep it running.`
                : 'Your turn-off request is with Lead Gurus. Spend continues until it\'s processed.'}
            </Alert>
          ) : null}
          {isSpentOut ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              This budget is fully spent for the month. Increase it below to keep leads coming.
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {onRequestChange && !isOff ? (
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={onRequestChange}>
                {isSpentOut ? 'Increase budget' : 'Adjust budget'}
              </Button>
            ) : null}
            {onTurnOff && !isOff && !isPendingOff ? (
              <Button color="warning" variant="text" onClick={onTurnOff}>
                Request turn-off
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Simple editable budget view for planning (future) months — set the amount and
// submit via the action bar below. No table for the 1-2 segment majority.
function SimplePlanCard({ segment, index, verticalOptions, locationOptions, rowStatus, isReadOnly, isFutureMonth, isCurrentMonth, updateSegment, previousAmount, rowError }) {
  const vertLabel = segment.vertical
    ? (verticalOptions.find((o) => String(o.value) === String(segment.vertical))?.label || null)
    : null;
  const locIds = segmentLocationIds(segment);
  const locLabel = locIds.length
    ? locIds.map((id) => locationOptions.find((o) => o.value === id)?.label).filter(Boolean).join(', ')
    : null;
  const scope = [vertLabel, locLabel].filter(Boolean).join(' · ');
  const delta = previousAmount !== null && previousAmount !== 0
    ? Number(segment.amount || 0) - previousAmount
    : null;
  const launch = getSegmentLaunch(segment);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              {scope || 'Your monthly budget'}
            </Typography>
            <Chip
              color={getStatusChipColor(rowStatus)}
              label={formatStatusLabel(rowStatus, { planned: isFutureMonth && String(rowStatus || '').toLowerCase() === 'draft' })}
              size="small"
              variant="filled"
            />
          </Stack>
          <Box>
            <Typography variant="overline" color="text.secondary">Monthly budget</Typography>
            <TextField
              disabled={isReadOnly}
              error={Boolean(rowError?.amount)}
              helperText={rowError?.amount || ''}
              fullWidth
              sx={{ maxWidth: 280, mt: 0.5 }}
              inputProps={{ inputMode: 'decimal', style: { fontSize: '1.75rem', fontWeight: 700, padding: '8px 12px' } }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              value={formatNumberInput(segment.amount)}
              onChange={(e) => updateSegment(index, 'amount', e.target.value.replace(/,/g, ''))}
            />
            {isCurrentMonth && Number(segment.month_to_date_spend || 0) > 0 && !rowError?.amount ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                min {formatCurrency(segment.month_to_date_spend)} — already spent this month
              </Typography>
            ) : null}
            {delta !== null ? (
              delta === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Same as last month
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: delta > 0 ? 'success.main' : 'error.main' }}>
                  was {formatCurrency(previousAmount)} last month
                </Typography>
              )
            ) : null}
            {launch ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {launch.caption}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Categorical palette for allocation slices — same family as the pacing hexes
// used elsewhere on this page.
const ALLOCATION_COLORS = ['#3b82f6', '#8b5cf6', '#14b8a6', '#f59e0b', '#ec4899', '#22c55e', '#0ea5e9', '#64748b'];

function AllocationBar({ title, slices, total }) {
  if (!slices.length || total <= 0) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{title}</Typography>
      <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', mt: 0.5, bgcolor: 'grey.200' }}>
        {slices.map((slice) => (
          <Box
            key={slice.key}
            sx={{
              width: `${(slice.amount / total) * 100}%`,
              bgcolor: slice.color,
              '&:not(:last-of-type)': { borderRight: '1px solid', borderColor: 'background.paper' },
            }}
          />
        ))}
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        {slices.map((slice) => (
          <Stack key={slice.key} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: slice.color, flexShrink: 0 }} />
            <Typography variant="caption">{slice.label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatCurrency(slice.amount)} · {Math.round((slice.amount / total) * 100)}%
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// "Where is my money going" header for multi-segment clients: one stacked bar
// by vertical and one by territory, sized by (effective) budget share.
function AllocationBreakdown({ segments, verticalOptions, locationOptions, useEffective }) {
  const amountOf = (seg) => Number((useEffective ? (seg.effective_amount || seg.amount) : seg.amount) || 0);
  const total = segments.reduce((sum, seg) => sum + amountOf(seg), 0);
  if (total <= 0 || segments.length < 2) return null;

  const territoryIds = (seg) => segmentLocationIds(seg).sort((a, b) => a - b);
  const groupBy = (keyFn, labelFn) => {
    const map = new Map();
    segments.forEach((seg) => {
      const amount = amountOf(seg);
      if (amount <= 0) return;
      const key = keyFn(seg);
      const entry = map.get(key) || { key, label: labelFn(seg), amount: 0 };
      entry.amount += amount;
      map.set(key, entry);
    });
    return Array.from(map.values())
      .sort((a, b) => b.amount - a.amount)
      .map((entry, i) => ({ ...entry, color: ALLOCATION_COLORS[i % ALLOCATION_COLORS.length] }));
  };
  const byVertical = groupBy(
    (seg) => String(seg.vertical || 'all'),
    (seg) => verticalOptions.find((o) => String(o.value) === String(seg.vertical))?.label || 'All verticals'
  );
  const byTerritory = groupBy(
    (seg) => territoryIds(seg).join(',') || 'all',
    (seg) => {
      const labels = territoryIds(seg)
        .map((id) => locationOptions.find((o) => Number(o.value) === id)?.label)
        .filter(Boolean);
      return labels.join(', ') || 'All territories';
    }
  );
  if (byVertical.length < 2 && byTerritory.length < 2) return null;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Where your budget is going</Typography>
        {byVertical.length > 1 ? <AllocationBar title="By vertical" slices={byVertical} total={total} /> : null}
        {byTerritory.length > 1 ? <AllocationBar title="By territory" slices={byTerritory} total={total} /> : null}
      </Stack>
    </Box>
  );
}

// Compact per-client card for the parent portfolio — the per-client decision
// surface (status, budget, pacing) at a glance, with drill-in to the drawer.
function PortfolioClientCard({ clientData, onOpen }) {
  const status = String(clientData.pageData?.status || 'draft').toLowerCase();
  const effective = clientData.segments.reduce(
    (sum, seg) => sum + Number(seg.effective_amount || seg.amount || 0), 0
  );
  const spend = clientData.segments.reduce(
    (sum, seg) => sum + Number(seg.month_to_date_spend || 0), 0
  );
  const pct = effective > 0 ? Math.round((spend / effective) * 100) : 0;
  const fillPct = effective > 0 ? Math.min(100, (spend / effective) * 100) : 0;
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }} onClick={onOpen}>
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>{clientData.clientName}</Typography>
            <Chip color={getStatusChipColor(status)} label={formatStatusLabel(status)} size="small" variant="filled" />
          </Stack>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h6" fontWeight={700}>{formatCurrency(effective)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {clientData.segments.length} segment{clientData.segments.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
          {spend > 0 ? (
            <Box>
              <Box sx={{ height: 6, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
                <Box sx={{ width: `${fillPct}%`, height: '100%', bgcolor: getPacingColor(pct) }} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatCurrency(spend)} spent · {pct}%
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

function WalkthroughOverlay({ stepIndex, steps, stepRefs, onNext, onBack, onSkip }) {
  const step = steps[stepIndex];
  const ref = stepRefs[step?.ref];
  const el = ref?.current;
  const [rect, setRect] = useState(null);

  // Lock body scroll while tour is active
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Auto-skip steps whose target element doesn't exist
  useEffect(() => {
    if (step && !ref?.current) {
      const timer = setTimeout(() => onNext(), 50);
      return () => clearTimeout(timer);
    }
  }, [step, ref, onNext]);

  useEffect(() => {
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Wait for scroll to settle then measure
      const timer = setTimeout(() => {
        setRect(el.getBoundingClientRect());
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [el, stepIndex]);

  if (!step || !el || !rect) return null;

  const isLast = stepIndex === steps.length - 1;
  const tooltipHeight = 180; // approximate tooltip height
  const spaceBelow = window.innerHeight - rect.bottom - 16;
  const placeAbove = spaceBelow < tooltipHeight && rect.top > tooltipHeight;
  const tooltipTop = placeAbove ? rect.top - tooltipHeight - 16 : rect.bottom + 16;

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onSkip}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.45)',
          zIndex: 1300,
          cursor: 'pointer',
        }}
      />
      {/* Highlight cutout */}
      <Box
        sx={{
          position: 'fixed',
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'primary.main',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
          zIndex: 1301,
          pointerEvents: 'none',
        }}
      />
      {/* Tooltip card */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: tooltipTop,
          left: Math.max(16, Math.min(rect.left, window.innerWidth - 380)),
          width: 360,
          zIndex: 1302,
          p: 2.5,
          borderRadius: 2,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>{step.title}</Typography>
            <Typography color="text.secondary" variant="caption">
              {stepIndex + 1} / {steps.length}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">{step.description}</Typography>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Button size="small" variant="text" onClick={onSkip}>Skip tour</Button>
            <Stack direction="row" spacing={1}>
              {stepIndex > 0 ? (
                <Button size="small" variant="text" onClick={onBack}>Back</Button>
              ) : null}
              <Button size="small" variant="contained" onClick={onNext}>
                {isLast ? 'Done' : 'Next'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}

export default function BudgetCenterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const realUser = state?.user;
  const { viewAsClient } = useClientView();

  // Staff preview mode: simulate a single-scope client member
  const isPreviewMode = Boolean(viewAsClient) && Boolean(realUser?.is_staff || realUser?.is_superuser);
  const user = isPreviewMode
    ? {
        ...realUser,
        is_staff: false,
        is_superuser: false,
        client_memberships: [
          {
            client_id: viewAsClient.id,
            client_name: viewAsClient.name,
            client_slug: viewAsClient.slug || viewAsClient.name,
            role: 'admin',
            budget_center_enabled: true,
          },
        ],
      }
    : realUser;
  const memberships = user?.client_memberships || [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Walkthrough
  const [walkthroughSeen, setWalkthroughSeen] = useLocalStorage('budgetCenter.walkthroughSeen', false);
  const [walkthroughSeenMulti, setWalkthroughSeenMulti] = useLocalStorage('budgetCenter.walkthroughSeenMulti', false);
  const [welcomeDismissed, setWelcomeDismissed] = useLocalStorage('budgetCenter.welcomeDismissed', false);
  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const walkthroughRefs = {
    clientSelector: useRef(null),
    monthNavigator: useRef(null),
    workflowStepper: useRef(null),
    budgetTable: useRef(null),
    actionBar: useRef(null),
    discussion: useRef(null),
    mcMonthNavigator: useRef(null),
    mcBudgetTable: useRef(null),
    mcSaveProgress: useRef(null),
  };
  const [selectedClient, setSelectedClient] = useState(searchParams.get('client_id') || '');
  const [selectedClientOption, setSelectedClientOption] = useState(null);
  const [staffParentCompanyId, setStaffParentCompanyId] = useState(searchParams.get('parent_company_id') || '');
  const [period, setPeriod] = useState(searchParams.get('period') || getCurrentMonthStart());
  const [pageData, setPageData] = useState(null);
  const [segments, setSegments] = useState([emptySegment(period)]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const [rowErrors, setRowErrors] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [adjustmentsDialogOpen, setAdjustmentsDialogOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [adjustmentError, setAdjustmentError] = useState('');
  const [adjustmentMode, setAdjustmentMode] = useState('total');
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [turnOffDialogSegment, setTurnOffDialogSegment] = useState(null);
  // Below-spend-floor resolution dialog: { indexes, requestFn, successMessage }
  const [spendFloorDialog, setSpendFloorDialog] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [markLiveDialogOpen, setMarkLiveDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [deleteRowIndex, setDeleteRowIndex] = useState(null);
  const [zeroAmountWarningOpen, setZeroAmountWarningOpen] = useState(false);
  // { onContinue } while the market-vs-all-territory warning is open.
  const [scopeMixWarning, setScopeMixWarning] = useState(null);
  // A plan handed over from the Forecast tab's "+20%" ladder. It only ever
  // PRE-FILLS the amounts — submissions auto-approve after a hold, so a button
  // on another page must never be able to commit money. The banner stays until
  // the user submits or navigates away.
  const [forecastPlanNote, setForecastPlanNote] = useState(null);
  const forecastPlanHandledRef = useRef(false);
  const [copyMonthDialogOpen, setCopyMonthDialogOpen] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [copySourcePeriod, setCopySourcePeriod] = useState('');
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [submitSuccessOpen, setSubmitSuccessOpen] = useState(false);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(null);
  // Segment list scroll target for the ?action=adjust deep link — attached to
  // whichever segment surface renders (desktop table or mobile card stack).
  const segmentsTableRef = useRef(null);
  const adjustActionHandledRef = useRef(false);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  // Spend lookup: { "vertId|locId" => { month_to_date_spend, projected_spend, ... } }
  const [spendMap, setSpendMap] = useState({});
  // Multi-client: search and collapse state
  const [multiClientSearch, setMultiClientSearch] = useState('');
  const [collapsedClients, setCollapsedClients] = useState({});
  // Multi-client dialog context: when set, dialogs operate on this client's data
  const [mcDialogClientId, setMcDialogClientId] = useState(null);
  // Parent-company drawer: opens a per-client detail panel
  const [drawerClientId, setDrawerClientId] = useState(null);
  const [drawerHistoryOpen, setDrawerHistoryOpen] = useState(false);
  // Tracks which locked (live) drawer segments are expanded into their full form.
  // Keyed by `${clientId}-${index}` so state doesn't bleed across clients.
  const [drawerExpandedSegments, setDrawerExpandedSegments] = useState({});
  const [drawerComments, setDrawerComments] = useState([]);
  const [drawerCommentText, setDrawerCommentText] = useState('');
  const [drawerCommentSaving, setDrawerCommentSaving] = useState(false);

  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const directMembershipClients = useMemo(
    () => memberships.filter((membership) => membership?.client_id),
    [memberships]
  );
  const isParentScopeMember = useMemo(
    () => !isStaff && memberships.length > 0 && !memberships[0]?.client_id && Boolean(
      memberships[0]?.parent_company_id || memberships[0]?.parent_company_name
    ),
    [isStaff, memberships]
  );
  const memberParentCompanyId = isParentScopeMember ? memberships[0]?.parent_company_id : undefined;
  const memberParentCompanyName = isParentScopeMember ? memberships[0]?.parent_company_name : '';
  const staffParentCompanyActive = isStaff && Boolean(staffParentCompanyId) && !selectedClient;
  const parentCompanyId = staffParentCompanyActive
    ? Number(staffParentCompanyId)
    : memberParentCompanyId;
  const isMultiClientMode = (isParentScopeMember && !isStaff) || staffParentCompanyActive;

  const {
    options: parentCompanyOptions,
    loading: parentCompanyLoading,
    findOptionByValue: findParentCompanyByValue,
  } = useParentCompanyAutocomplete({ skip: !isStaff });
  const staffParentCompanyName = staffParentCompanyActive
    ? (findParentCompanyByValue(staffParentCompanyId)?.label || '')
    : '';
  const parentCompanyName = memberParentCompanyName || staffParentCompanyName;

  const {
    options: clientOptions,
    loading: clientLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete({ parentCompanyId, hideGroupedBranches: true });

  // Multi-client hook for parent scope members
  const multiClient = useMultiClientBudgetCenter({
    clientOptions,
    period,
    enabled: isMultiClientMode,
    parentCompanyId,
  });

  // Feature flag: parent scope members always see it (flag checked per-client on load);
  // direct members need at least one client with the flag enabled
  const clientBudgetCenterEnabled = isStaff || isParentScopeMember || directMembershipClients.some((m) => m.budget_center_enabled);
  const accessibleClientCount = isStaff
    ? clientOptions.length
    : directMembershipClients.length > 0
      ? directMembershipClients.length
      : clientOptions.length;
  const canShowClientSelector = isStaff;
  const singleClientMembership = !isStaff && !isParentScopeMember && directMembershipClients.length === 1
    ? directMembershipClients[0]
    : null;
  const readOnlyClientName = pageData?.client?.name
    || selectedClientOption?.label
    || singleClientMembership?.client_name
    || '';

  // Vertical/territory options fetch — keyed on the client ONLY. Depending on
  // option/search identities refetched these (territories are the heaviest
  // payload for multi-territory clients) on every month click and keystroke.
  useEffect(() => {
    if (!selectedClient) {
      setVerticalOptions([]);
      setLocationOptions([]);
      return;
    }
    api.request({
      url: `dashboard/client-budgets/budget-verticals/?client_id=${selectedClient}`,
      method: 'GET',
    })
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
        setVerticalOptions(rows.map((row) => ({ value: row.id, label: row.vertical })));
      })
      .catch(() => setVerticalOptions([]));
    api.request({
      url: 'dashboard/client-territories/',
      method: 'GET',
      payload: { client_id: selectedClient },
    })
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
        setLocationOptions(rows.map((row) => ({
          value: row.id,
          label: row.name,
          verticalIds: territoryVerticalIds(row),
        })));
      })
      .catch(() => setLocationOptions([]));
  }, [selectedClient]);

  // Keep the Autocomplete's selected option label in sync once options load.
  useEffect(() => {
    if (!selectedClient) return;
    const option = findOptionByValue(selectedClient);
    if (option) {
      setSelectedClientOption(option);
    }
  }, [findOptionByValue, selectedClient]);

  // Initial client fallback — apply at most ONCE. Re-running it re-selected
  // the stale URL client_id every time staff cleared the selector.
  const clientFallbackDoneRef = useRef(false);
  useEffect(() => {
    if (clientFallbackDoneRef.current) return;
    if (selectedClient) {
      clientFallbackDoneRef.current = true;
      return;
    }
    // When staff has the parent-company filter active, don't auto-collapse into
    // a single client — they want the multi-client rollup.
    if (staffParentCompanyActive) {
      return;
    }
    const fallbackClientId = searchParams.get('client_id')
      || memberships.find((membership) => membership.client_id)?.client_id
      || (clientOptions.length === 1 ? String(clientOptions[0].value) : '');
    if (fallbackClientId) {
      clientFallbackDoneRef.current = true;
      setSelectedClient(String(fallbackClientId));
    }
  }, [clientOptions, memberships, searchParams, selectedClient, staffParentCompanyActive]);

  // When staff preview mode changes, auto-select the previewed client
  useEffect(() => {
    if (isPreviewMode && viewAsClient?.id) {
      setSelectedClient(String(viewAsClient.id));
    }
  }, [isPreviewMode, viewAsClient?.id]);

  const loadSeqRef = useRef(0);
  const loadBudgetCenter = useCallback(() => {
    if (!selectedClient || isMultiClientMode) {
      return;
    }

    // Guard against out-of-order responses: switching client/month while a
    // slower request is in flight must not render the old scope's data (a
    // save would then post the wrong client's segments).
    const seq = ++loadSeqRef.current;

    const normalizePayloadSegments = (payload) => (
      Array.isArray(payload?.segments) && payload.segments.length
        ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
        : [emptySegment(payload?.period || period)]
    );
    const applyBcPayload = (rawPayload) => {
      const payload = sortPayloadSegments(rawPayload);
      setPageData(payload);
      setSegments(normalizePayloadSegments(payload));
      setRowErrors([]);
    };
    // Revalidate after a cache hit: refresh pageData, but never clobber
    // segment edits the user made while the background request ran.
    // Both payloads get the same display sort as applyBcPayload so the
    // prev-vs-baseline comparison lines up index-for-index.
    const applyBcPayloadPreservingEdits = (rawPayload, rawBaselinePayload) => {
      const payload = sortPayloadSegments(rawPayload);
      const baselinePayload = sortPayloadSegments(rawBaselinePayload);
      setPageData(payload);
      setSegments((prev) => {
        const baseline = normalizePayloadSegments(baselinePayload);
        const prevKey = JSON.stringify(prev.map(segmentDraftComparable));
        const baselineKey = JSON.stringify(baseline.map(segmentDraftComparable));
        return prevKey === baselineKey ? normalizePayloadSegments(payload) : prev;
      });
    };
    const applySpendData = (rows) => {
      const map = {};
      for (const row of (rows || [])) {
        const key = `${row.vertical_id || ''}|${row.location_id || ''}`;
        map[key] = { month_to_date_spend: row.month_to_date_spend || 0 };
      }
      setSpendMap(map);
    };

    const cachedBc = peekBudgetCenter({ client_id: selectedClient, period });
    const cachedScope = peekScopeSpend({ client_id: selectedClient, period });
    const hasCachedBc = cachedBc != null;

    if (hasCachedBc) {
      applyBcPayload(cachedBc);
      applySpendData(cachedScope || []);
      setLoading(false);
    } else {
      setLoading(true);
      setPageData(null);
      setBanner(null);
      setSelectedSegmentIndex(null);
      setComments([]);
      setCommentText('');
      setAdjustmentsDialogOpen(false);
      setNotesDialogOpen(false);
      setHistoryOpen(false);
    }

    Promise.all([
      fetchBudgetCenter({ client_id: selectedClient, period }),
      fetchBudgetCenterScopeSpend({
        client_id: selectedClient,
        period,
      }).catch(() => ({ data: [] })),
    ])
      .then(([bcResponse, spendResponse]) => {
        if (loadSeqRef.current !== seq) return;
        if (hasCachedBc) {
          applyBcPayloadPreservingEdits(bcResponse.data, cachedBc);
        } else {
          applyBcPayload(bcResponse.data);
        }
        applySpendData(spendResponse.data || []);
      })
      .catch((error) => {
        if (loadSeqRef.current !== seq) return;
        if (!hasCachedBc) {
          setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to load Budget Center.') });
        }
      })
      .finally(() => {
        if (loadSeqRef.current !== seq) return;
        setLoading(false);
      });
  }, [period, selectedClient, isMultiClientMode]);

  useEffect(() => {
    loadBudgetCenter();
  }, [loadBudgetCenter]);

  // ── Turn-off hold window ─────────────────────────────────────────────────
  // Turn-offs auto-apply after a short window (server-configured, never
  // hardcoded here). While it runs the client can undo the WHOLE batch —
  // all-or-nothing, because the window is debounced per client and one
  // deadline covers every segment in it.
  const turnOffBatch = pageData?.turn_off_batch || null;
  const turnOffDeadline = turnOffBatch?.auto_applies_at || null;
  const [turnOffNow, setTurnOffNow] = useState(() => Date.now());
  const [turnOffCancelOpen, setTurnOffCancelOpen] = useState(false);
  const [turnOffCanceling, setTurnOffCanceling] = useState(false);

  useEffect(() => {
    if (!turnOffDeadline) return undefined;
    setTurnOffNow(Date.now());
    const timer = setInterval(() => setTurnOffNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [turnOffDeadline]);

  const turnOffSecondsLeft = useMemo(() => {
    if (!turnOffDeadline) return null;
    const ms = dayjs(turnOffDeadline).valueOf() - turnOffNow;
    return Math.max(0, Math.ceil(ms / 1000));
  }, [turnOffDeadline, turnOffNow]);

  const turnOffCountdownLabel = useMemo(() => {
    if (turnOffSecondsLeft == null) return '';
    const mins = Math.floor(turnOffSecondsLeft / 60);
    const secs = turnOffSecondsLeft % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  }, [turnOffSecondsLeft]);

  // Window elapsed → the batch has executed server-side. Stop counting and
  // refetch ONCE so the rows settle into their real (off) state rather than
  // sitting at 0:00 forever. Small grace period: the worker fires at the
  // deadline, and its propagation runs after that.
  //
  // The already-refetched guard is keyed to the deadline and lives at module
  // scope on purpose. A ref (or state) resets when this page remounts — which
  // is exactly what a refetch causes — so the refetch re-armed itself and the
  // page polled forever whenever the worker hadn't cleared the row yet
  // (backed-up queue, auto-approval disabled). One refetch per deadline, ever.
  useEffect(() => {
    if (!turnOffDeadline || turnOffSecondsLeft !== 0) return undefined;
    if (settledTurnOffDeadlines.has(turnOffDeadline)) return undefined;
    settledTurnOffDeadlines.add(turnOffDeadline);
    const timeout = setTimeout(() => {
      bustBudgetCenterCache({ client_id: selectedClient, period });
      loadBudgetCenter();
    }, 5000);
    return () => clearTimeout(timeout);
  }, [turnOffDeadline, turnOffSecondsLeft, selectedClient, period, loadBudgetCenter]);

  const confirmCancelTurnOff = useCallback(async () => {
    setTurnOffCanceling(true);
    try {
      await cancelBudgetCenterTurnOff({ client_id: Number(selectedClient), period });
      setTurnOffCancelOpen(false);
      setBanner(null);
      setSnackbar({ open: true, message: 'Turn-off canceled — nothing changed.', severity: 'success' });
      bustBudgetCenterCache({ client_id: selectedClient, period });
      loadBudgetCenter();
    } catch (error) {
      setTurnOffCancelOpen(false);
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to cancel the turn-off.') });
      // The most likely failure is "the window already elapsed" — refetch so
      // the page stops offering an Undo that can't work.
      bustBudgetCenterCache({ client_id: selectedClient, period });
      loadBudgetCenter();
    } finally {
      setTurnOffCanceling(false);
    }
  }, [selectedClient, period, loadBudgetCenter]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (isMultiClientMode) {
      nextParams.delete('client_id');
    } else if (selectedClient) {
      nextParams.set('client_id', String(selectedClient));
    } else {
      nextParams.delete('client_id');
    }
    if (staffParentCompanyActive) {
      nextParams.set('parent_company_id', String(staffParentCompanyId));
    } else {
      nextParams.delete('parent_company_id');
    }
    if (period) {
      nextParams.set('period', period);
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [isMultiClientMode, period, searchParams, selectedClient, setSearchParams, staffParentCompanyActive, staffParentCompanyId]);

  // Auto-start walkthrough for first-time visitors once data is loaded
  useEffect(() => {
    if (walkthroughActive) return;
    if (isMultiClientMode) {
      if (!walkthroughSeenMulti && !multiClient.loading && (multiClient.clientEntries || []).length > 0) {
        const timer = setTimeout(() => setWalkthroughActive(true), 600);
        return () => clearTimeout(timer);
      }
    } else if (!walkthroughSeen && !loading && pageData && selectedClient) {
      const timer = setTimeout(() => setWalkthroughActive(true), 600);
      return () => clearTimeout(timer);
    }
  }, [isMultiClientMode, walkthroughSeen, walkthroughSeenMulti, loading, pageData, selectedClient, walkthroughActive, multiClient.loading, multiClient.clientEntries]);

  const walkthroughSteps = isMultiClientMode
    ? WALKTHROUGH_STEPS_MULTI_CLIENT
    : isStaff
      ? WALKTHROUGH_STEPS_STAFF
      : WALKTHROUGH_STEPS_CLIENT;

  const endWalkthrough = useCallback(() => {
    setWalkthroughActive(false);
    setWalkthroughStep(0);
    if (isMultiClientMode) {
      setWalkthroughSeenMulti(true);
    } else {
      setWalkthroughSeen(true);
    }
  }, [isMultiClientMode, setWalkthroughSeen, setWalkthroughSeenMulti]);

  const nextWalkthroughStep = useCallback(() => {
    if (walkthroughStep >= walkthroughSteps.length - 1) {
      endWalkthrough();
    } else {
      setWalkthroughStep((prev) => prev + 1);
    }
  }, [endWalkthrough, walkthroughStep, walkthroughSteps.length]);

  const prevWalkthroughStep = useCallback(() => {
    setWalkthroughStep((prev) => Math.max(0, prev - 1));
  }, []);

  const currentSummary = pageData?.current_budget || null;
  const previousSummary = pageData?.previous_budget || null;
  const isCurrentMonth = dayjs(period).isSame(dayjs(), 'month');
  const isFutureMonth = dayjs(period).isAfter(dayjs(), 'month');
  const isPastMonth = dayjs(period).isBefore(dayjs(), 'month');
  const isReadOnly = isPastMonth && !isStaff;
  const hasLiveBudgetRows = Array.isArray(pageData?.live_budget_ids)
    && pageData.live_budget_ids.some((id) => {
      const matchingRow = (pageData?.segments || []).find((segment) => Number(segment.budget_id) === Number(id));
      return String(matchingRow?.workflow_status || '').toLowerCase() === 'active';
    });
  const isLiveMonth = isCurrentMonth && (
    hasLiveBudgetRows || String(pageData?.status || '').toLowerCase() === 'active'
  );
  const isApprovedPendingLive = String(pageData?.status || '').toLowerCase() === 'approved';
  const isPendingApproval = String(pageData?.status || '').toLowerCase() === 'pending_approval';
  const showAdjustmentColumns = isLiveMonth;
  // Mode 1 (simple view): clients running 1-2 segments get a simplified card
  // instead of the full editing table — a read-friendly pacing card on the live
  // month, an editable amount card on planning (future) months. Staff and
  // parent-scope (portfolio) views always use the detailed table; past months
  // keep the read-only table.
  const simpleViewEligible = !isStaff && !isMultiClientMode && !isPastMonth
    && segments.length > 0 && segments.length <= 2;
  const allSegmentsOff = showAdjustmentColumns
    && segments.length > 0
    && segments.every((segment) => String(getSegmentOperationalStatus(segment)).toLowerCase() === 'off');

  // Dashboard quick action: /budget-center?action=adjust deep-links into the
  // budget-change flow. Handled ONCE after the first single-client load — the
  // simple view opens the segment-adjustments dialog exactly like its "Adjust
  // budget" button; the table view just scrolls to the segment list. The param
  // is stripped afterward so a refresh doesn't re-trigger it.
  useEffect(() => {
    if (adjustActionHandledRef.current) return;
    if (searchParams.get('action') !== 'adjust') return;
    const stripActionParam = () => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('action');
      setSearchParams(nextParams, { replace: true });
    };
    // Portfolio (multi-client) mode never loads single-client pageData — the
    // quick action is client-scoped, so just drop the param there.
    if (isMultiClientMode) {
      adjustActionHandledRef.current = true;
      stripActionParam();
      return;
    }
    if (loading || !pageData) return;
    adjustActionHandledRef.current = true;
    if (simpleViewEligible && !showDetails && segments.length > 0) {
      setSelectedSegmentIndex(0);
      setAdjustmentsDialogOpen(true);
    } else {
      segmentsTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    stripActionParam();
  }, [
    isMultiClientMode,
    loading,
    pageData,
    searchParams,
    segments.length,
    setSearchParams,
    showDetails,
    simpleViewEligible,
  ]);
  // Apply a forecast plan once the segments have loaded: match on budget id and
  // overwrite the amount, leaving every other field alone. Mirrors the
  // ?action=adjust deep link — the param is stripped so a refresh doesn't
  // silently re-apply a stale plan over edits someone has since made.
  useEffect(() => {
    if (forecastPlanHandledRef.current) return;
    const raw = searchParams.get('plan');
    if (searchParams.get('action') !== 'forecast_plan' || !raw) return;
    const stripPlanParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      next.delete('plan');
      setSearchParams(next, { replace: true });
    };
    if (isMultiClientMode) {
      forecastPlanHandledRef.current = true;
      stripPlanParams();
      return;
    }
    if (loading || !pageData) return;
    forecastPlanHandledRef.current = true;
    try {
      const pairs = JSON.parse(window.atob(raw));
      const byId = new Map(pairs.map(([id, amount]) => [Number(id), Number(amount)]));
      let applied = 0;
      setSegments((prev) => prev.map((segment) => {
        const id = Number(segment.source_budget_id ?? segment.budget_id);
        if (!byId.has(id)) return segment;
        applied += 1;
        return { ...segment, amount: String(byId.get(id)) };
      }));
      setForecastPlanNote(
        applied
          ? `${applied} market${applied === 1 ? '' : 's'} pre-filled from the forecast. `
            + 'Nothing is submitted yet — review the amounts and submit as usual.'
          : 'That forecast plan didn\'t match any budget lines on this month.'
      );
    } catch (err) {
      setForecastPlanNote('That forecast plan could not be read.');
    }
    stripPlanParams();
  }, [
    isMultiClientMode,
    loading,
    pageData,
    searchParams,
    setSearchParams,
  ]);

  // Vertical group subheaders for the detailed table: rows arrive pre-sorted
  // by vertical (sortSegmentRows), so a subheader wherever the vertical
  // changes gives multi-vertical clients a grouped read. Single-vertical
  // clients skip the chrome entirely.
  const showVerticalGroupHeaders = useMemo(
    () => new Set(segments.map((segment) => String(segment.vertical || ''))).size > 1,
    [segments]
  );
  const optionSet = useMemo(
    () => ({ verticals: verticalOptions, locations: locationOptions }),
    [verticalOptions, locationOptions]
  );
  const selectedSegment = selectedSegmentIndex === null ? null : segments[selectedSegmentIndex] || null;

  // Multi-client dialog helpers: when mcDialogClientId is set, derive segment/options from multi-client data
  const mcClientData = mcDialogClientId ? multiClient.clientDataMap[mcDialogClientId] : null;
  const dialogSegment = mcDialogClientId
    ? (selectedSegmentIndex !== null && mcClientData ? mcClientData.segments[selectedSegmentIndex] || null : null)
    : selectedSegment;
  const dialogOptionSet = mcDialogClientId && mcClientData
    ? { verticals: mcClientData.verticalOptions || [], locations: mcClientData.locationOptions || [] }
    : optionSet;

  const approvalTotal = useMemo(
    () => segments.reduce((total, segment) => total + Number(segment.amount || 0), 0),
    [segments]
  );
  const effectiveTotal = useMemo(
    () => segments.reduce((total, segment) => total + Number(segment.effective_amount || segment.amount || 0), 0),
    [segments]
  );
  const previousAmount = Number(previousSummary?.effective_amount ?? previousSummary?.amount ?? 0);
  const currentAmountForDelta = showAdjustmentColumns ? effectiveTotal : approvalTotal;
  const monthDelta = previousAmount ? currentAmountForDelta - previousAmount : null;
  const monthDeltaPct = previousAmount ? ((monthDelta / previousAmount) * 100) : null;
  // The month-over-month total delta is apples-to-oranges when the segment makeup
  // changed (e.g. planning a new month with fewer segments than last month) — it
  // reads as a huge scary drop. Suppress the comparison in that case.
  const currentSegmentCount = currentSummary?.segment_count || segments.length || 0;
  const previousSegmentCount = Number(previousSummary?.segment_count || 0);
  const segmentCompositionChanged = previousSegmentCount > 0
    && previousSegmentCount !== currentSegmentCount;

  // Per-segment previous-month lookup for delta hints
  const previousSegments = pageData?.previous_segments || [];
  const getPreviousSegmentAmount = useCallback((segment) => {
    if (!previousSegments.length) return null;
    const match = previousSegments.find(
      (prev) =>
        String(prev.vertical_id || '') === String(segment.vertical || '')
        && String(prev.location_id || '') === String(segment.location || '')
        && String(prev.channel_id || '') === String(segment.channel || '')
    );
    return match ? Number(match.amount) : null;
  }, [previousSegments]);

  // Check if segments are completely unchanged from what was loaded (seed or prior save)
  const segmentsUnchangedFromLoad = useMemo(() => {
    const loaded = Array.isArray(pageData?.segments) ? pageData.segments : [];
    if (!loaded.length || loaded.length !== segments.length) return false;
    return segments.every((seg, i) => {
      const orig = loaded[i];
      return String(seg.amount || 0) === String(orig.amount || orig.starting_budget || 0)
        && String(seg.vertical || '') === String(orig.vertical || orig.vertical_id || '')
        && String(seg.location || '') === String(orig.location || orig.location_id || '');
    });
  }, [pageData?.segments, segments]);

  // Progress checklist state
  const progressSteps = useMemo(() => {
    const status = String(pageData?.status || '').toLowerCase();
    const hasAmounts = segments.some((s) => Number(s.amount || 0) > 0);
    return {
      reviewed: hasAmounts,
      saved: pageData?.has_draft || status !== 'draft',
      submitted: ['pending_approval', 'approved', 'active'].includes(status),
    };
  }, [pageData?.status, pageData?.has_draft, segments]);
  // Edits made in this browser that aren't persisted anywhere yet — this is
  // what the leave-page warning should key on. A server-side saved draft is
  // NOT at risk on tab close.
  const hasLocalSegmentEdits = useMemo(() => {
    const loadedSegments = Array.isArray(pageData?.segments) ? pageData.segments : [];
    if (loadedSegments.length !== segments.length) {
      return true;
    }
    const currentComparable = JSON.stringify(segments.map(segmentDraftComparable));
    const loadedComparable = JSON.stringify(
      loadedSegments.map((segment) => segmentDraftComparable(normalizeSegment(segment, pageData?.period || period)))
    );
    return currentComparable !== loadedComparable;
  }, [pageData, period, segments]);
  const hasDraftChanges = useMemo(() => {
    // An unsubmitted server draft still needs submitting — keep the Submit
    // button visible for it. A pending_approval budget reports has_draft=true
    // server-side, but the client has already submitted it — treating it as
    // dirty wrongly re-shows the Submit button.
    if (pageData?.has_unsubmitted_draft) {
      return true;
    }
    return hasLocalSegmentEdits;
  }, [pageData?.has_unsubmitted_draft, hasLocalSegmentEdits]);
  const dirtyMultiClientIds = useMemo(() => {
    if (!isMultiClientMode) return [];
    return (multiClient.clientEntries || [])
      .filter(([, data]) => {
        const status = String(data?.pageData?.status || 'draft').toLowerCase();
        if (status === 'active') return false;
        return computeClientHasDraftChanges(data, period);
      })
      .map(([clientId]) => clientId);
  }, [isMultiClientMode, multiClient.clientEntries, period]);
  const saveAllMultiClientProgress = useCallback(async () => {
    if (!dirtyMultiClientIds.length) return;
    const results = await Promise.allSettled(
      dirtyMultiClientIds.map((clientId) => multiClient.saveDraftForClient(clientId))
    );
    const failures = results.filter((r) => r.status === 'rejected').length;
    if (failures === 0) {
      setSnackbar({ open: true, message: `Progress saved for ${results.length} client${results.length === 1 ? '' : 's'}.`, severity: 'success' });
    } else if (failures === results.length) {
      setSnackbar({ open: true, message: 'Couldn’t save progress. Please try again.', severity: 'error' });
    } else {
      setSnackbar({ open: true, message: `Saved ${results.length - failures} of ${results.length}. ${failures} failed — see row errors.`, severity: 'warning' });
    }
  }, [dirtyMultiClientIds, multiClient]);
  const approvalActionLabel = isStaff
    ? (isPendingApproval
      ? 'Approve Budget'
      : isLiveMonth ? 'Approve Changes' : 'Approve Budget')
    : (isLiveMonth ? 'Submit Changes' : 'Submit for Review');
  const saveDraftLabel = isStaff ? 'Save Draft' : 'Save Progress';

  // Warn user before leaving with unsaved changes — local edits only. A saved
  // (server-side) draft survives tab close; warning on it trained users to
  // ignore the prompt.
  useEffect(() => {
    const handler = (event) => {
      if (!hasLocalSegmentEdits) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasLocalSegmentEdits]);

  const buildPayload = useCallback((segmentList = segments) => {
    // For live months, only send segments that are new or actually changed.
    // Unchanged live-origin segments stay untouched — no draft cloning needed.
    const loadedSegments = Array.isArray(pageData?.segments) ? pageData.segments : [];
    const segmentsToSubmit = isLiveMonth
      ? segmentList.filter((segment) => {
        // New segment (no budget_id) — always include
        if (!segment.budget_id) return true;
        // Already a draft — always include
        if (pageData?.draft_budget_ids?.includes(segment.budget_id)) return true;
        // Live-origin: check if anything changed vs loaded version
        const loaded = loadedSegments.find(
          (s) => (s.budget_id || s.id) === segment.budget_id
        );
        if (!loaded) return true;
        // Compare key fields — including the full territory set, not just the
        // first territory, so multi-territory edits are submitted.
        return (
          String(segment.amount || '') !== String(loaded.amount || '')
          || String(segmentVerticalId(segment)) !== String(segmentVerticalId(loaded))
          || segmentLocationKey(segment) !== segmentLocationKey(loaded)
          || String(segment.notes || '') !== String(loaded.notes || '')
        );
      })
      : segmentList;

    return {
      client_id: Number(selectedClient),
      period,
      allocation_mode: 'optimize_freely',
      allocation_config: {},
      notes: '',
      segments: segmentsToSubmit.map((segment) => ({
        budget_id: segment.budget_id || null,
        source_budget_id: segment.source_budget_id || null,
        vertical: segmentVerticalId(segment) || null,
        vertical_ids: segmentVerticalId(segment) ? [Number(segmentVerticalId(segment))] : [],
        location: segment.location || segmentLocationIds(segment)[0] || null,
        location_ids: segmentLocationIds(segment),
        channel: segment.channel || null,
        amount: segment.amount,
        starts_on: segment.starts_on || null,
        notes: segment.notes || '',
      })),
    };
  }, [isLiveMonth, pageData?.live_budget_ids, period, segments, selectedClient]);

  const validateSegments = useCallback((segmentList = segments) => {
    const nextRowErrors = segmentList.map(() => ({}));
    const seenKeys = new Map();
    // Effective amount each saved row carried when the page loaded. The spend
    // floor only guards REDUCTIONS — a row that drifted below its own spend on
    // its own (Facebook overdelivered) must not block the rest of the table.
    const loadedEffectiveById = new Map();
    (Array.isArray(pageData?.segments) ? pageData.segments : []).forEach((row) => {
      const id = row?.budget_id ?? row?.id;
      if (id === null || id === undefined) return;
      loadedEffectiveById.set(
        Number(id),
        Number(row.effective_amount ?? row.amount ?? 0)
      );
    });

    segmentList.forEach((segment, index) => {
      if (segment.amount === '' || segment.amount === null || segment.amount === undefined) {
        nextRowErrors[index].amount = 'Starting budget is required.';
      } else if (Number.isNaN(Number(segment.amount))) {
        nextRowErrors[index].amount = 'Enter a valid number.';
      } else if (Number(segment.amount) < 0) {
        nextRowErrors[index].amount = 'Starting budget cannot be negative.';
      } else if (isCurrentMonth && !segment.has_pending_turn_off && !segment.is_turned_off) {
        // Mirror the server's spend floor (pending and completed turn-off rows
        // excluded on both sides): mid-month, a segment's effective budget
        // can't drop below what its scope has already spent. Turned-off rows
        // settle at turn-off time but spend can keep attributing afterward,
        // which would otherwise block every other edit in the table.
        const spendFloor = Number(segment.month_to_date_spend || 0);
        const effective = Number(segment.amount) + Number(segment.adjustment_total || 0);
        // No baseline (a brand-new row) means nothing is being reduced — the
        // spend on that scope belongs to the segment that was already there.
        const baseline = loadedEffectiveById.has(Number(segment.budget_id))
          ? loadedEffectiveById.get(Number(segment.budget_id))
          : loadedEffectiveById.get(Number(segment.source_budget_id));
        const isReduction = baseline !== undefined && effective < baseline - 0.005;
        if (isReduction && spendFloor > 0 && effective < spendFloor - 0.005) {
          nextRowErrors[index].amount = `Already spent ${formatCurrency(spendFloor)} this month — the budget can't go below that.`;
          nextRowErrors[index].belowSpendFloor = true;
        }
      }

      // Duplicate detection keys on the full sorted territory set — first-pick
      // order shouldn't matter, and [A,B] vs [A,C] are distinct segments.
      const territoryKey = segmentLocationKey(segment);
      const key = `${segment.vertical || 'all'}|${territoryKey || 'all'}|${segment.channel || 'all'}`;
      if (seenKeys.has(key)) {
        nextRowErrors[index].segment = 'This segment already exists in the table.';
        const firstIndex = seenKeys.get(key);
        nextRowErrors[firstIndex].segment = 'This segment already exists in the table.';
      } else {
        seenKeys.set(key, index);
      }
    });

    setRowErrors(nextRowErrors);
    return nextRowErrors;
  }, [isCurrentMonth, pageData?.segments, segments]);

  const runBudgetAction = useCallback(async (requestFn, successMessage, segmentsOverride = null) => {
    if (!selectedClient) {
      setBanner({ severity: 'error', message: 'Select a client first.' });
      return null;
    }
    const activeSegments = segmentsOverride || segments;
    const validationErrors = validateSegments(activeSegments);
    if (validationErrors.some((row) => Object.keys(row).length)) {
      const floorIndexes = validationErrors
        .map((row, index) => (row.belowSpendFloor ? index : -1))
        .filter((index) => index >= 0);
      // A below-spend amount almost always means "stop spending here" — when
      // that's the only problem, offer to settle at spend instead of erroring.
      const onlyFloorErrors = validationErrors.every((row) => {
        const keys = Object.keys(row).filter((key) => key !== 'belowSpendFloor');
        return !keys.length || (row.belowSpendFloor && keys.length === 1 && keys[0] === 'amount');
      });
      if (floorIndexes.length && onlyFloorErrors) {
        setSpendFloorDialog({ indexes: floorIndexes, requestFn, successMessage });
        return null;
      }
      setBanner({ severity: 'error', message: 'Fix the highlighted rows before continuing.' });
      return null;
    }
    const payload = buildPayload(activeSegments);
    if (!payload.segments.length) {
      setBanner({ severity: 'info', message: 'No changes to submit.' });
      return null;
    }
    setSaving(true);
    try {
      const response = await requestFn(payload);
      const responseData = sortPayloadSegments(response.data);
      setPageData(responseData);
      setSegments(
        Array.isArray(responseData?.segments) && responseData.segments.length
          ? responseData.segments.map((segment) => normalizeSegment(segment, responseData.period || period))
          : [emptySegment(period)]
      );
      setRowErrors([]);
      setBanner(null);
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      return responseData;
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Budget action failed.') });
      return null;
    } finally {
      setSaving(false);
    }
  }, [buildPayload, period, segments, selectedClient, validateSegments]);

  // "Stop spend" resolution: settle each below-floor segment's budget at its
  // month-to-date spend (remaining drops to $0, so delivery stops for the rest
  // of the month) and re-run the action the user originally clicked.
  const confirmSpendFloorSettle = useCallback(async () => {
    if (!spendFloorDialog) return;
    const { indexes, requestFn, successMessage } = spendFloorDialog;
    const settled = segments.map((segment, index) => {
      if (!indexes.includes(index)) return segment;
      const spendFloor = Number(segment.month_to_date_spend || 0);
      const adjustments = Number(segment.adjustment_total || 0);
      return { ...segment, amount: Math.max(0, spendFloor - adjustments).toFixed(2) };
    });
    setSegments(settled);
    setSpendFloorDialog(null);
    await runBudgetAction(requestFn, successMessage, settled);
  }, [runBudgetAction, segments, spendFloorDialog]);

  const saveDraft = useCallback(
    () => runBudgetAction(saveBudgetCenterDraft, 'Draft saved.'),
    [runBudgetAction]
  );
  const approveDraft = useCallback(
    async () => {
      const result = await runBudgetAction(
        approveBudgetCenter,
        isStaff
          ? 'Budget approved. Mark it live when ready.'
          : 'Budget submitted for review. Lead Gurus will review and make it live.'
      );
      if (result && !isStaff) {
        setSubmitSuccessOpen(true);
      }
      return result;
    },
    [isStaff, runBudgetAction]
  );
  const markLive = useCallback(
    async () => {
      if (!selectedClient) {
        setBanner({ severity: 'error', message: 'Select a client first.' });
        return;
      }
      setSaving(true);
      try {
        const response = await markBudgetCenterLive({
          client_id: Number(selectedClient),
          period,
        });
        const payload = sortPayloadSegments(response.data);
        setPageData(payload);
        setSegments(
          Array.isArray(payload?.segments) && payload.segments.length
            ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
            : [emptySegment(period)]
        );
        setBanner(null);
        setSnackbar({ open: true, message: 'Budget marked live.', severity: 'success' });
      } catch (error) {
        setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to mark budget live.') });
      } finally {
        setSaving(false);
      }
    },
    [period, selectedClient]
  );

  const discardDraft = useCallback(async () => {
    if (!selectedClient) {
      return;
    }
    setSaving(true);
    try {
      const response = await discardBudgetCenterDraft({
        client_id: Number(selectedClient),
        period,
      });
      const payload = sortPayloadSegments(response.data);
      setPageData(payload);
      setSegments(
        Array.isArray(payload?.segments) && payload.segments.length
          ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
          : [emptySegment(period)]
      );
      setBanner(null);
      setSnackbar({ open: true, message: 'Changes discarded.', severity: 'success' });
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to discard draft.') });
    } finally {
      setSaving(false);
    }
  }, [period, selectedClient]);

  const updateSegment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, rowIndex) => {
      if (rowIndex !== index) return segment;
      const updated = { ...segment, [field]: value };
      // Switching the vertical narrows the market list — drop any picked
      // market that doesn't run the new vertical instead of leaving it
      // attached but invisible in the (now scoped) picker.
      if (field === 'vertical' && value) {
        const currentIds = segmentLocationIds(segment);
        const allowed = new Set(
          scopedLocationOptions(locationOptions, value).map((option) => Number(option.value))
        );
        const keptIds = currentIds.filter((id) => allowed.has(id));
        if (keptIds.length !== currentIds.length) {
          updated.location_ids = keptIds;
          updated.location = keptIds[0] || '';
        }
      }
      // When vertical or location changes, look up MTD spend from the scope-spend map
      if (field === 'vertical' || field === 'location') {
        const vertId = field === 'vertical' ? value : segment.vertical;
        const locId = field === 'location' ? value : (updated.location ?? segment.location);
        // Exact scope only — falling back to vertical-wide or whole-client
        // spend painted fresh segments as deep-red "overspent" with money
        // that belongs to a broader scope.
        const spend = spendMap[`${vertId || ''}|${locId || ''}`];
        updated.month_to_date_spend = spend?.month_to_date_spend || 0;
        // The old scope's pacing metrics no longer describe this row.
        updated.pacing_percentage = null;
        updated.projected_spend = 0;
        updated.remaining_per_day = 0;
      }
      return updated;
    }));
    setRowErrors((prev) => prev.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const next = { ...(row || {}) };
      if (field === 'amount') {
        delete next.amount;
        delete next.belowSpendFloor;
      }
      delete next.segment;
      return next;
    }));
  };

  const updatePendingAdjustment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, rowIndex) => (
      rowIndex === index
        ? {
          ...segment,
          pendingAdjustment: {
            ...(segment.pendingAdjustment || {}),
            [field]: value,
          },
        }
        : segment
    )));
  };

  const duplicateSegment = useCallback((index) => {
    setSegments((prev) => {
      const source = prev[index];
      if (!source) {
        return prev;
      }
      const copy = cloneSegment(source);
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
    setRowErrors((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      next.splice(index + 1, 0, {});
      return next;
    });
  }, []);

  const addAdjustment = useCallback(async () => {
    if (saving) return;
    const seg = mcDialogClientId ? dialogSegment : selectedSegment;
    if (selectedSegmentIndex === null || !seg) {
      return;
    }
    const pendingAdjustment = seg.pendingAdjustment || {};
    // The date field displays a default when the state is empty — submit what
    // the user sees instead of erroring on the blank state.
    const effectiveDate = pendingAdjustment.effective_date || getDefaultAdjustmentDate(period);
    if (!pendingAdjustment.amount_delta) {
      setAdjustmentError('Adjustment amount is required.');
      return;
    }

    const budgetId = seg.budget_id;
    if (!budgetId) {
      setAdjustmentError('Save the budget first before adding adjustments.');
      return;
    }

    setAdjustmentError('');
    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${budgetId}/adjustments/`,
        method: 'POST',
        payload: { ...pendingAdjustment, effective_date: effectiveDate },
      });
      setAdjustmentError('');
      // Forward-month changes fold into the base amount server-side (no
      // adjustment row) — say so instead of "Adjustment added".
      const isFutureMonth = dayjs(period).startOf('month').isAfter(dayjs().startOf('month'));
      setSnackbar({
        open: true,
        message: isFutureMonth ? 'Starting budget updated.' : 'Adjustment added.',
        severity: 'success',
      });
      setAdjustmentsDialogOpen(false);
      setSelectedSegmentIndex(null);
      // Drop the now-stale cached payload so the reload can't flash the
      // pre-adjustment numbers from cache.
      bustBudgetCenterCache({ client_id: mcDialogClientId || selectedClient, period });
      if (mcDialogClientId) {
        setMcDialogClientId(null);
        multiClient.reload();
      } else {
        loadBudgetCenter();
      }
    } catch (error) {
      setAdjustmentError(getErrorMessage(error, 'Failed to add adjustment.'));
    } finally {
      setSaving(false);
    }
  }, [dialogSegment, loadBudgetCenter, mcDialogClientId, multiClient, period, saving, selectedClient, selectedSegment, selectedSegmentIndex]);

  const deleteAdjustment = useCallback(async (adjustmentId) => {
    if (saving) return;
    const seg = mcDialogClientId ? dialogSegment : selectedSegment;
    const mcClientPageData = mcDialogClientId ? multiClient.clientDataMap[mcDialogClientId]?.pageData : null;
    const effectivePageData = mcDialogClientId ? mcClientPageData : pageData;
    if (!seg?.budget_id || !effectivePageData?.draft_budget_ids?.includes(seg.budget_id)) {
      setBanner({ severity: 'info', message: 'Save a draft before removing adjustments.' });
      return;
    }
    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${seg.budget_id}/adjustments/${adjustmentId}/`,
        method: 'DELETE',
      });
      setBanner(null);
      setSnackbar({ open: true, message: 'Adjustment removed.', severity: 'success' });
      bustBudgetCenterCache({ client_id: mcDialogClientId || selectedClient, period });
      if (mcDialogClientId) {
        multiClient.reload();
      } else {
        loadBudgetCenter();
      }
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to remove adjustment.') });
    } finally {
      setSaving(false);
    }
  }, [dialogSegment, loadBudgetCenter, mcDialogClientId, multiClient, pageData, period, saving, selectedClient, selectedSegment]);

  const turnOffSegment = useCallback(async (segment) => {
    if (!segment || saving) {
      return;
    }
    const effectiveAmount = Number(segment.effective_amount || segment.amount || 0);
    const monthToDateSpend = Number(segment.month_to_date_spend || 0);
    // Turn-off drops the effective budget down to month-to-date spend. When the
    // segment is already at/over spend there is nothing to reduce, so clamp to 0:
    // the turn-off (campaign pause + turned_off_at) still fires, and we never
    // raise the budget number to paper over an overspend (leave it visible).
    const amountDelta = Math.min(0, monthToDateSpend - effectiveAmount);

    const budgetId = segment.budget_id;
    if (!budgetId) {
      setBanner({ severity: 'error', message: 'Save the budget first before turning off segments.' });
      return;
    }

    const clientIdForTurnOff = mcDialogClientId || selectedClient;
    setSaving(true);
    try {
      if (isStaff) {
        // Staff can turn off directly
        await api.request({
          url: `dashboard/client-budgets/${budgetId}/adjustments/`,
          method: 'POST',
          payload: {
            amount_delta: amountDelta.toFixed(2),
            effective_date: dayjs().format('YYYY-MM-DD'),
            notes: 'Turn off segment',
          },
        });
        setBanner(null);
        setSnackbar({ open: true, message: 'Segment turned off.', severity: 'success' });
      } else {
        // Clients submit a turn-off; it auto-applies after a short hold window
        await api.request({
          url: 'dashboard/client-budgets/budget-center/request-turn-off/',
          method: 'POST',
          payload: {
            client_id: Number(clientIdForTurnOff),
            period,
            budget_id: budgetId,
          },
        });
        setBanner(null);
        setSnackbar({ open: true, message: 'Turn-off submitted — it takes effect within about 15 minutes.', severity: 'success' });
      }
      bustBudgetCenterCache({ client_id: clientIdForTurnOff, period });
      if (mcDialogClientId) {
        multiClient.reload();
      } else {
        loadBudgetCenter();
      }
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to turn off segment.') });
    } finally {
      setSaving(false);
    }
  }, [isStaff, loadBudgetCenter, mcDialogClientId, multiClient, period, saving, selectedClient]);

  // Staff-only override: bring a turned-off segment back on. Restores the
  // original effective budget and (if the turn-off auto-paused campaigns)
  // opens the campaign-resume prompt. Safe to apply directly — no dialog —
  // since turning back on only ever raises the budget.
  const turnOnSegment = useCallback(async (segment, { clientId: mcClientId } = {}) => {
    if (saving) return;
    const budgetId = segment?.budget_id;
    if (!budgetId) {
      setBanner({ severity: 'error', message: 'Save the budget first before turning segments back on.' });
      return;
    }
    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${budgetId}/turn-on/`,
        method: 'POST',
      });
      setBanner(null);
      setSnackbar({ open: true, message: 'Segment turned back on.', severity: 'success' });
      bustBudgetCenterCache({ client_id: mcClientId || selectedClient, period });
      if (mcClientId) {
        multiClient.reload();
      } else {
        loadBudgetCenter();
      }
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to turn segment back on.') });
    } finally {
      setSaving(false);
    }
  }, [loadBudgetCenter, multiClient, period, saving, selectedClient]);

  const openTurnOffDialog = useCallback((segment) => {
    setTurnOffDialogSegment(segment || null);
  }, []);

  const closeTurnOffDialog = useCallback(() => {
    setTurnOffDialogSegment(null);
  }, []);

  const confirmTurnOffSegment = useCallback(async () => {
    if (!turnOffDialogSegment) {
      return;
    }
    await turnOffSegment(turnOffDialogSegment);
    setTurnOffDialogSegment(null);
    setMcDialogClientId(null);
  }, [turnOffDialogSegment, turnOffSegment]);

  const openNotesDialog = useCallback((index) => {
    setSelectedSegmentIndex(index);
    setNotesDialogOpen(true);
  }, []);

  const closeNotesDialog = useCallback(() => {
    setNotesDialogOpen(false);
  }, []);

  // Before a submit: warn when it would put market budgets beside an
  // all-territory budget (or the reverse). Compared against what's live, so a
  // saved-but-unsubmitted draft still warns and staff approving a client's
  // submission see it too.
  const guardScopeMix = useCallback((segmentList, loadedPageData, proceed) => {
    const loaded = Array.isArray(loadedPageData?.segments) ? loadedPageData.segments : [];
    const liveIds = new Set((loadedPageData?.live_budget_ids || []).map(Number));
    const live = loaded.filter((s) => liveIds.has(Number(s.budget_id ?? s.id)));
    if (!findScopeMixSegments(segmentList, live.length ? live : loaded).length) {
      proceed();
      return;
    }
    setScopeMixWarning({ onContinue: proceed });
  }, []);

  const openApproveDialog = useCallback(() => {
    if (validateSegments().some((row) => Object.keys(row).length)) {
      setBanner({ severity: 'error', message: 'Fix the highlighted rows before continuing.' });
      return;
    }
    guardScopeMix(segments, pageData, () => {
      const hasZeroSegments = segments.some((s) => Number(s.amount) === 0);
      if (hasZeroSegments) {
        setZeroAmountWarningOpen(true);
        return;
      }
      setApproveDialogOpen(true);
    });
  }, [guardScopeMix, pageData, segments, validateSegments]);

  const closeApproveDialog = useCallback(() => {
    setApproveDialogOpen(false);
  }, []);

  const confirmApproveDraft = useCallback(async () => {
    setApproveDialogOpen(false);
    await approveDraft();
  }, [approveDraft]);

  const confirmMarkLive = useCallback(async () => {
    setMarkLiveDialogOpen(false);
    await markLive();
  }, [markLive]);

  const confirmDiscard = useCallback(async () => {
    setDiscardDialogOpen(false);
    await discardDraft();
  }, [discardDraft]);

  const confirmDeleteRow = useCallback(() => {
    if (deleteRowIndex === null) return;
    setSegments((prev) => prev.filter((_, rowIndex) => rowIndex !== deleteRowIndex));
    setRowErrors((prev) => prev.filter((_, rowIndex) => rowIndex !== deleteRowIndex));
    setDeleteRowIndex(null);
  }, [deleteRowIndex]);

  const openCopyMonthDialog = useCallback(async () => {
    if (!selectedClient) return;
    try {
      const response = await fetchAvailableMonths({ client_id: selectedClient });
      setAvailableMonths(response.data || []);
      setCopySourcePeriod('');
      setCopyMonthDialogOpen(true);
    } catch {
      setBanner({ severity: 'error', message: 'Failed to load available months.' });
    }
  }, [selectedClient]);

  const confirmCopyFromMonth = useCallback(async () => {
    if (!selectedClient || !copySourcePeriod) return;
    setCopyMonthDialogOpen(false);
    setLoading(true);
    try {
      const response = await copyFromMonth({
        client_id: selectedClient,
        target_period: period,
        source_period: copySourcePeriod,
      });
      const data = response.data;
      if (Array.isArray(data?.segments) && data.segments.length) {
        // Same display sort as the load path — copied rows are local edits
        // (pageData untouched), so only the segments array needs sorting.
        setSegments(sortSegmentRows(data.segments).map((s) => normalizeSegment(s, period)));
        setSnackbar({ open: true, message: `Copied ${data.segments.length} segment(s) from ${copySourcePeriod}.`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'No segments found for the selected month.', severity: 'info' });
      }
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to copy segments.') });
    } finally {
      setLoading(false);
    }
  }, [selectedClient, copySourcePeriod, period]);

  const loadComments = useCallback(async () => {
    if (!selectedClient || !period) return;
    try {
      const response = await fetchBudgetComments({ client_id: selectedClient, period });
      setComments(response.data || []);
    } catch {
      setComments([]);
    }
  }, [selectedClient, period]);

  const submitComment = useCallback(async () => {
    if (!commentText.trim() || !selectedClient) return;
    setCommentSaving(true);
    try {
      await postBudgetComment({ client_id: selectedClient, period, body: commentText.trim() });
      setCommentText('');
      await loadComments();
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to post comment.') });
    } finally {
      setCommentSaving(false);
    }
  }, [commentText, selectedClient, period, loadComments]);

  // Load comments when client/period changes
  useEffect(() => {
    if (selectedClient && period) {
      loadComments();
    } else {
      setComments([]);
    }
  }, [selectedClient, period, loadComments]);

  const loadDrawerComments = useCallback(async (clientId) => {
    if (!clientId || !period) return;
    try {
      const response = await fetchBudgetComments({ client_id: Number(clientId), period });
      setDrawerComments(response.data || []);
    } catch {
      setDrawerComments([]);
    }
  }, [period]);

  const submitDrawerComment = useCallback(async () => {
    if (!drawerCommentText.trim() || !drawerClientId) return;
    setDrawerCommentSaving(true);
    try {
      await postBudgetComment({
        client_id: Number(drawerClientId),
        period,
        body: drawerCommentText.trim(),
      });
      setDrawerCommentText('');
      await loadDrawerComments(drawerClientId);
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error, 'Failed to post comment.'), severity: 'error' });
    } finally {
      setDrawerCommentSaving(false);
    }
  }, [drawerClientId, drawerCommentText, period, loadDrawerComments]);

  // Load drawer comments when drawer client changes
  useEffect(() => {
    if (drawerClientId) {
      loadDrawerComments(drawerClientId);
    } else {
      setDrawerComments([]);
      setDrawerCommentText('');
      setDrawerHistoryOpen(false);
    }
  }, [drawerClientId, loadDrawerComments]);

  if (!clientBudgetCenterEnabled) {
    return (
      <Page title="Budget Center" fullWidth>
        <Stack spacing={3} alignItems="center" sx={{ py: 10 }}>
          <Typography variant="h5">Budget Center</Typography>
          <Typography color="text.secondary" variant="body1" textAlign="center">
            Budget Center is not yet enabled for your account. Contact Lead Gurus if you&apos;d like to start managing your budgets here.
          </Typography>
        </Stack>
      </Page>
    );
  }

  return (
    <Page title="Budget Center" fullWidth>
      <Stack spacing={2}>
        {banner ? (
          <Alert onClose={() => setBanner(null)} severity={banner.severity}>
            {banner.message}
          </Alert>
        ) : null}

        {/* Handed over from the Forecast tab's ladder. Pre-fill only — the
            amounts are sitting in the form, not committed. */}
        {forecastPlanNote ? (
          <Alert severity="info" onClose={() => setForecastPlanNote(null)}>
            {forecastPlanNote}
          </Alert>
        ) : null}

        {/* Turn-off hold window: one countdown + one all-or-nothing Undo for
            the whole batch. Disappears the moment the window elapses. */}
        {turnOffBatch?.can_cancel && turnOffSecondsLeft > 0 ? (
          <Alert
            severity="warning"
            icon={<UndoOutlinedIcon fontSize="inherit" />}
            action={(
              <Button
                color="inherit"
                size="small"
                onClick={() => setTurnOffCancelOpen(true)}
              >
                Undo
              </Button>
            )}
          >
            {turnOffBatch.pending_count} segment{turnOffBatch.pending_count === 1 ? '' : 's'} turning off in <strong>{turnOffCountdownLabel}</strong>. Undo now to keep {turnOffBatch.pending_count === 1 ? 'it' : 'them'} running — nothing has changed yet.
          </Alert>
        ) : null}

        {selectedClient ? <ActivePacingHoldsBanner clientId={Number(selectedClient)} /> : null}

        {/* Staff: keep the full client selector */}
        {canShowClientSelector ? (
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    loading={parentCompanyLoading}
                    options={parentCompanyOptions}
                    value={findParentCompanyByValue(staffParentCompanyId)}
                    onChange={(_, option) => {
                      const next = option?.value ? String(option.value) : '';
                      setStaffParentCompanyId(next);
                      if (next) {
                        // Switch to multi-client parent rollup view
                        setSelectedClient('');
                        setSelectedClientOption(null);
                      }
                    }}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                    renderInput={(params) => <TextField {...params} label="Parent Company" placeholder="All brands" />}
                  />
                </Grid>
                <Grid ref={walkthroughRefs.clientSelector} item xs={12} md={4}>
                  <Autocomplete
                    loading={clientLoading}
                    onChange={(_, option) => {
                      setSelectedClient(option?.value ? String(option.value) : '');
                      setSelectedClientOption(option || null);
                      if (option?.value && staffParentCompanyId) {
                        // Picking a specific client exits the parent rollup view
                        setStaffParentCompanyId('');
                      }
                    }}
                    onInputChange={handleClientSearch}
                    options={clientOptions}
                    value={selectedClientOption}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                    renderOption={(props, option) => (
                      <li {...props} key={option?.value ?? option?.label}>
                        {option?.label || ''}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Client"
                        placeholder={staffParentCompanyActive ? 'Pick a brand to drill in' : 'Select client'}
                      />
                    )}
                  />
                </Grid>
                <Grid ref={walkthroughRefs.monthNavigator} item xs={12} md={3}>
                  <MonthNavigator isStaff={isStaff} onChange={(newPeriod) => setPeriod(newPeriod)} value={period} />
                </Grid>
                <Grid item xs={12} md={2}>
                  {staffParentCompanyActive ? (
                    <Chip
                      color="info"
                      label={`${clientOptions.length} brand${clientOptions.length === 1 ? '' : 's'}`}
                      variant="filled"
                    />
                  ) : (
                    <Chip
                      color={getStatusChipColor(pageData?.status)}
                      label={formatStatusLabel(pageData?.status, { planned: isFutureMonth && String(pageData?.status || '').toLowerCase() === 'draft' })}
                      variant="filled"
                    />
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ) : null}

        {/* Staff-only: funded segments with nothing to target. The API adds
            targeting_gaps only for staff viewers, so clients never see this. */}
        {isStaff && Array.isArray(pageData?.targeting_gaps) && pageData.targeting_gaps.length > 0 ? (
          <Alert severity="error" icon={<WarningAmberOutlinedIcon />} sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {pageData.targeting_gaps.length === 1
                ? '1 funded segment has no targetable ZIPs'
                : `${pageData.targeting_gaps.length} funded segments have no targetable ZIPs`}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Budget is live but nothing will spend until the ZIPs are resumed or added, or the segment is turned off.
            </Typography>
            <Stack component="ul" spacing={0.25} sx={{ pl: 2, mt: 0.5, mb: 0 }}>
              {pageData.targeting_gaps.map((gap) => (
                <Typography component="li" variant="body2" key={`${gap.budget_id}-${gap.territory_id}-${gap.vertical_id || 'all'}`}>
                  <strong>{gap.territory}{gap.vertical ? ` ${gap.vertical}` : ''}</strong>
                  {' — $'}{Number(gap.amount).toLocaleString()}{' — '}
                  {TARGETING_GAP_CAUSE[gap.cause] || gap.cause}
                </Typography>
              ))}
            </Stack>
          </Alert>
        ) : null}

        {/* Multi-client mode: show all clients in one view */}
        {isMultiClientMode ? (
          <>
            {/* Month header — mirrors the single-client layout */}
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
                      {parentCompanyName ? (
                        <Typography variant="subtitle1" fontWeight={700}>
                          {parentCompanyName}
                        </Typography>
                      ) : null}
                      <Box ref={walkthroughRefs.mcMonthNavigator}>
                        <MonthNavigator isStaff={isStaff} onChange={(newPeriod) => setPeriod(newPeriod)} value={period} />
                      </Box>
                      <Tooltip title="Refresh data">
                        <IconButton size="small" onClick={() => multiClient.reload()} disabled={multiClient.loading}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        variant="text"
                        onClick={() => { setWalkthroughStep(0); setWalkthroughActive(true); }}
                      >
                        Tour
                      </Button>
                    </Stack>
                  </Stack>

                  {/* Inline summary bar */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 3 }}
                    divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
                  >
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary">Budget</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{formatCurrency(multiClient.aggregates.totalBudget)}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary">Segments</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{multiClient.aggregates.totalSegments}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary">Clients</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{multiClient.aggregates.clientCount}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      {Object.entries(multiClient.aggregates.statusCounts)
                        .map(([status, count]) => `${count} ${formatStatusLabel(status)}`)
                        .join(', ') || 'No data'}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {multiClient.loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : multiClient.error ? (
              <Alert severity="error">{multiClient.error}</Alert>
            ) : (
              <>
                {/* Per-client overview cards — the per-client decision surface;
                    the grouped table below stays for segment-level work */}
                {multiClient.clientEntries.length > 1 ? (
                  <Grid container spacing={1.5}>
                    {multiClient.clientEntries.map(([clientId, clientData]) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={`portfolio-card-${clientId}`}>
                        <PortfolioClientCard
                          clientData={clientData}
                          onOpen={() => setDrawerClientId(clientId)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : null}

                {/* Multi-client grouped table */}
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5}>
                        <Box>
                          <Typography variant="h6">Monthly Budget Table</Typography>
                          <Typography color="text.secondary" variant="body2">
                            All clients for {formatMonthLabel(period)}. Each client has its own workflow.
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {dirtyMultiClientIds.length > 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              {dirtyMultiClientIds.length} client{dirtyMultiClientIds.length === 1 ? '' : 's'} with unsaved edits
                            </Typography>
                          ) : null}
                          <Button
                            ref={walkthroughRefs.mcSaveProgress}
                            size="small"
                            variant="outlined"
                            startIcon={<SaveOutlinedIcon />}
                            disabled={dirtyMultiClientIds.length === 0 || multiClient.loading}
                            onClick={saveAllMultiClientProgress}
                          >
                            {saveDraftLabel}
                          </Button>
                          <TextField
                            size="small"
                            placeholder="Search clients..."
                            value={multiClientSearch}
                            onChange={(e) => setMultiClientSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            sx={{ minWidth: 220 }}
                          />
                        </Stack>
                      </Stack>
                      <TableContainer ref={walkthroughRefs.mcBudgetTable} component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto', maxHeight: '70vh' }}>
                        <Table>
                          <TableHead>
                            <TableRow
                              sx={{
                                bgcolor: 'var(--mui-palette-background-level1)',
                                '& th': { position: 'sticky', top: 0, zIndex: 2, bgcolor: 'var(--mui-palette-background-level1)' },
                              }}
                            >
                              <TableCell>Client</TableCell>
                              <TableCell>Vertical</TableCell>
                              <TableCell>Location</TableCell>
                              <TableCell>Starting Budget</TableCell>
                              <TableCell>Adjustments</TableCell>
                              <TableCell>Effective Budget</TableCell>
                              <TableCell>MTD Spend</TableCell>
                              <TableCell>Remaining</TableCell>
                              <TableCell>Remaining/Day</TableCell>
                              <TableCell>Pacing</TableCell>
                              <TableCell>Projected</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {multiClient.clientEntries
                              .filter(([, clientData]) =>
                                !multiClientSearch.trim()
                                || (clientData.clientName || '').toLowerCase().includes(multiClientSearch.trim().toLowerCase())
                              )
                              .map(([clientId, clientData]) => {
                              const clientStatus = String(clientData.pageData?.status || 'draft').toLowerCase();
                              const clientTotal = clientData.segments.reduce(
                                (sum, s) => sum + Number(s.amount || 0), 0
                              );
                              const effectiveTotal = clientData.segments.reduce(
                                (sum, s) => sum + Number(s.effective_amount || s.amount || 0), 0
                              );
                              const isPendingApprovalClient = clientStatus === 'pending_approval';
                              const isApprovedClient = clientStatus === 'approved';
                              const isActiveClient = clientStatus === 'active';
                              const isCurrentMonthClient = dayjs(period).isSame(dayjs(), 'month');
                              const clientHasLiveRows = Array.isArray(clientData.pageData?.live_budget_ids)
                                && clientData.pageData.live_budget_ids.some((id) => {
                                  const matchingRow = (clientData.pageData?.segments || []).find((s) => Number(s.budget_id) === Number(id));
                                  return String(matchingRow?.workflow_status || '').toLowerCase() === 'active';
                                });
                              const isClientLiveMonth = isCurrentMonthClient && (clientHasLiveRows || isActiveClient);
                              const clientHasDraftChanges = computeClientHasDraftChanges(clientData, period);
                              return (
                                <React.Fragment key={clientId}>
                                  {/* Client group header */}
                                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell colSpan={13}>
                                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                          <Typography fontWeight={700} variant="subtitle2">
                                            {clientData.clientName}
                                          </Typography>
                                          <Chip
                                            color={getStatusChipColor(clientStatus)}
                                            label={formatStatusLabel(clientStatus)}
                                            size="small"
                                            variant="filled"
                                          />
                                          <Typography variant="caption" color="text.secondary">
                                            {clientData.segments.length} segment{clientData.segments.length !== 1 ? 's' : ''}
                                            {' '}&middot; {formatCurrency(clientTotal)} budget
                                            {effectiveTotal !== clientTotal ? ` · ${formatCurrency(effectiveTotal)} effective` : ''}
                                          </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                          {isStaff && isApprovedClient ? (
                                            <Button
                                              size="small"
                                              color="success"
                                              variant="contained"
                                              disabled={clientData.saving}
                                              onClick={() => multiClient.markLiveForClient(clientId).catch(() => {})}
                                            >
                                              Mark Live
                                            </Button>
                                          ) : null}
                                          {/* Staff approve a submitted budget; everyone submits drafts.
                                              An approved budget gets NO submit button — re-submitting
                                              demoted it back to pending. */}
                                          {isStaff && isPendingApprovalClient ? (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              disabled={clientData.saving}
                                              onClick={() => guardScopeMix(
                                                clientData.segments,
                                                clientData.pageData,
                                                () => multiClient.approveForClient(clientId).catch(() => {})
                                              )}
                                            >
                                              Approve
                                            </Button>
                                          ) : null}
                                          {!isActiveClient && !isPendingApprovalClient
                                            && (!isApprovedClient || clientHasDraftChanges) ? (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              disabled={clientData.saving}
                                              onClick={() => guardScopeMix(
                                                clientData.segments,
                                                clientData.pageData,
                                                () => multiClient.approveForClient(clientId).catch(() => {})
                                              )}
                                            >
                                              {isApprovedClient ? 'Submit Changes' : 'Submit'}
                                            </Button>
                                          ) : null}
                                          {(!isActiveClient || isClientLiveMonth) ? (
                                            <Tooltip title="Add a new budget segment">
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<AddIcon />}
                                                onClick={() => {
                                                  multiClient.addSegmentForClient(clientId);
                                                  setDrawerClientId(clientId);
                                                }}
                                              >
                                                Add segment
                                              </Button>
                                            </Tooltip>
                                          ) : null}
                                          <Tooltip title="Open full budget details">
                                            <IconButton
                                              size="small"
                                              onClick={() => setDrawerClientId(clientId)}
                                            >
                                              <OpenInFullIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          {clientData.saving ? <CircularProgress size={20} /> : null}
                                        </Stack>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                  {clientData.error ? (
                                    <TableRow>
                                      <TableCell colSpan={13}>
                                        <Alert severity="error" onClose={() => multiClient.clearErrorForClient(clientId)}>
                                          {clientData.error}
                                        </Alert>
                                      </TableCell>
                                    </TableRow>
                                  ) : null}
                                  {clientData.loadError && !clientData.pageData ? (
                                    <TableRow>
                                      <TableCell colSpan={13}>
                                        <Alert severity="warning">
                                          Budget data for {clientData.clientName} failed to load — amounts shown may be incomplete. Try refreshing.
                                        </Alert>
                                      </TableCell>
                                    </TableRow>
                                  ) : null}
                                  {/* Segment rows for this client */}
                                  {clientData.segments.map((segment, index) => {
                                    const segLaunch = getSegmentLaunch(segment);
                                    const segScheduled = Boolean(segLaunch?.pacingNote) && segLaunch.phase === 'scheduled';
                                    const segRemaining = Number(segment.effective_amount || segment.amount || 0) - Number(segment.month_to_date_spend || 0);
                                    const segOverspent = segRemaining < 0 && !segScheduled;
                                    return (
                                    <TableRow key={`${clientId}-${segment.budget_id || index}`} hover sx={{ '& td': { py: 1.5 }, ...(segOverspent ? { bgcolor: 'rgba(211, 47, 47, 0.06)', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.12) !important' } } : {}) }}>
                                      <TableCell>
                                        {index === 0 ? (
                                          <Typography variant="body2" color="text.secondary">
                                            {clientData.clientName}
                                          </Typography>
                                        ) : null}
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          disabled={isActiveClient}
                                          select
                                          size="small"
                                          fullWidth
                                          value={segment.vertical}
                                          onChange={(e) => multiClient.updateSegmentForClient(clientId, index, 'vertical', e.target.value)}
                                        >
                                          <MenuItem value="">All</MenuItem>
                                          {(clientData.verticalOptions || []).map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                          ))}
                                        </TextField>
                                      </TableCell>
                                      <TableCell sx={{ minWidth: 200 }}>
                                        <Autocomplete
                                          multiple
                                          size="small"
                                          disabled={isActiveClient}
                                          options={scopedLocationOptions(
                                            clientData.locationOptions || [],
                                            segment.vertical,
                                            segmentLocationIds(segment),
                                          )}
                                          getOptionLabel={(opt) => opt.label || ''}
                                          value={(clientData.locationOptions || []).filter((o) => segmentLocationIds(segment).includes(o.value))}
                                          onChange={(_, val) => {
                                            const ids = (val || []).map((item) => item.value);
                                            multiClient.updateSegmentForClient(clientId, index, 'location_ids', ids);
                                            multiClient.updateSegmentForClient(clientId, index, 'location', ids[0] || '');
                                          }}
                                          isOptionEqualToValue={(option, value) => option.value === value.value}
                                          renderInput={(params) => <TextField {...params} placeholder={segmentLocationIds(segment).length ? '' : 'All'} />}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ minWidth: 140 }}>
                                        <TextField
                                          disabled={isActiveClient}
                                          size="small"
                                          fullWidth
                                          inputProps={{ inputMode: 'decimal' }}
                                          InputProps={{
                                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                          }}
                                          value={formatNumberInput(segment.amount)}
                                          onChange={(e) => multiClient.updateSegmentForClient(clientId, index, 'amount', e.target.value.replace(/,/g, ''))}
                                        />
                                        {segLaunch ? (
                                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            {segLaunch.caption}
                                          </Typography>
                                        ) : null}
                                      </TableCell>
                                      <TableCell>{formatCurrency(segment.adjustment_total)}</TableCell>
                                      <TableCell>{formatCurrency(segment.effective_amount || segment.amount)}</TableCell>
                                      <TableCell>{formatCurrency(segment.month_to_date_spend)}</TableCell>
                                      <TableCell sx={segOverspent ? { color: 'error.main', fontWeight: 600 } : undefined}>
                                        {formatCurrency(segRemaining)}
                                      </TableCell>
                                      <TableCell sx={!segment.is_turned_off && segOverspent ? { color: 'error.main', fontWeight: 600 } : undefined}>{segment.is_turned_off || segScheduled ? '—' : formatCurrency(segment.remaining_per_day)}</TableCell>
                                      <TableCell>
                                        <PacingCell effectiveAmount={segment.effective_amount || segment.amount} monthToDateSpend={segment.month_to_date_spend} pacingPercentage={segment.pacing_percentage} isTurnedOff={segment.is_turned_off} launchNote={segLaunch?.pacingNote} />
                                      </TableCell>
                                      <TableCell>{segLaunch?.pacingNote ? '—' : formatCurrency(segment.projected_spend)}</TableCell>
                                      <TableCell>
                                        {(() => {
                                          const mcRowStatus = getBudgetCenterRowStatus(segment, {
                                            isLiveMonth: isClientLiveMonth,
                                            liveBudgetIds: clientData.pageData?.live_budget_ids,
                                          }) || clientStatus;
                                          return (
                                            <Chip
                                              color={getStatusChipColor(mcRowStatus)}
                                              label={formatStatusLabel(mcRowStatus)}
                                              size="small"
                                              variant="filled"
                                            />
                                          );
                                        })()}
                                      </TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setMcDialogClientId(clientId);
                                            setSelectedSegmentIndex(index);
                                            setNotesDialogOpen(true);
                                          }}
                                        >
                                          {segment.notes ? 'Edit Notes' : 'Notes'}
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => multiClient.duplicateSegmentForClient(clientId, index)}
                                        >
                                          Duplicate
                                        </Button>
                                        {isClientLiveMonth && isExistingSegment(segment) ? (
                                          <Button
                                            color="primary"
                                            size="small"
                                            startIcon={<EditOutlinedIcon />}
                                            sx={{ mx: 0.5 }}
                                            variant="contained"
                                            onClick={() => {
                                              setMcDialogClientId(clientId);
                                              setSelectedSegmentIndex(index);
                                              setAdjustmentsDialogOpen(true);
                                            }}
                                          >
                                            Add Adjustment
                                          </Button>
                                        ) : null}
                                        {isClientLiveMonth && isExistingSegment(segment) ? (
                                          isStaff && segment.is_turned_off ? (
                                            <Button
                                              color="success"
                                              size="small"
                                              onClick={() => turnOnSegment(segment, { clientId })}
                                            >
                                              Turn Back On
                                            </Button>
                                          ) : segment.has_pending_turn_off && !isStaff ? (
                                            <Chip label={turnOffChipLabel(segment)} size="small" color="warning" variant="outlined" />
                                          ) : !segment.is_turned_off ? (
                                            <Button
                                              size="small"
                                              onClick={() => {
                                                setMcDialogClientId(clientId);
                                                setTurnOffDialogSegment(segment);
                                              }}
                                            >
                                              Turn Off
                                            </Button>
                                          ) : null
                                        ) : null}
                                        <Tooltip title={clientData.segments.length === 1 ? 'At least one segment is required' : (isClientLiveMonth && isLiveOriginSegment(segment, clientData.pageData?.live_budget_ids)) ? 'Live segments cannot be deleted' : 'Remove segment'}>
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => multiClient.deleteSegmentForClient(clientId, index)}
                                              disabled={(isClientLiveMonth && isLiveOriginSegment(segment, clientData.pageData?.live_budget_ids)) || clientData.segments.length === 1}
                                            >
                                              <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        ) : null}

        {/* Per-client detail drawer (parent-company view) */}
        {isMultiClientMode ? (
          <Drawer
            anchor="right"
            open={Boolean(drawerClientId)}
            onClose={() => setDrawerClientId(null)}
            PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 640 } } }}
          >
            {(() => {
              if (!drawerClientId) return null;
              const data = multiClient.clientDataMap[drawerClientId];
              if (!data) {
                return (
                  <Box p={3}>
                    <Typography color="text.secondary">Loading…</Typography>
                  </Box>
                );
              }
              const drawerStatus = String(data.pageData?.status || 'draft').toLowerCase();
              const drawerIsPending = drawerStatus === 'pending_approval';
              const drawerIsActive = drawerStatus === 'active';
              const drawerHasDraftChanges = computeClientHasDraftChanges(data, period);
              const drawerLiveBudgetIds = data.pageData?.live_budget_ids;
              const drawerIsLiveMonth = dayjs(period).isSame(dayjs(), 'month')
                && Array.isArray(drawerLiveBudgetIds) && drawerLiveBudgetIds.length > 0;
              // On a live month, only the already-live segments are locked; a newly added
              // segment stays editable and is submitted as a change for staff review — the
              // same path the single-client view uses. Non-live active budgets stay fully
              // locked, so this adds no new past-month editing.
              const drawerSegmentLocked = (segment) => {
                if (!drawerIsActive) return false;
                if (!drawerIsLiveMonth) return true;
                return isLiveOriginSegment(segment, drawerLiveBudgetIds);
              };
              const drawerCanAddSegment = !drawerIsActive || drawerIsLiveMonth;
              const drawerTotal = (data.segments || []).reduce(
                (sum, s) => sum + Number(s.amount || 0),
                0,
              );
              return (
                <Stack sx={{ height: '100%' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                      <Typography variant="h6" noWrap>{data.clientName}</Typography>
                      <Chip
                        size="small"
                        color={getStatusChipColor(drawerStatus)}
                        label={formatStatusLabel(drawerStatus)}
                      />
                    </Stack>
                    <IconButton onClick={() => setDrawerClientId(null)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Stack>

                  <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
                    <Stack spacing={3}>
                      <Stack direction="row" spacing={3} divider={<Divider orientation="vertical" flexItem />}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Budget</Typography>
                          <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(drawerTotal)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Segments</Typography>
                          <Typography variant="subtitle1" fontWeight={700}>{(data.segments || []).length}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Month</Typography>
                          <Typography variant="subtitle1" fontWeight={700}>{formatMonthLabel(period)}</Typography>
                        </Box>
                      </Stack>

                      {data.error ? (
                        <Alert severity="error" onClose={() => multiClient.clearErrorForClient(drawerClientId)}>
                          {data.error}
                        </Alert>
                      ) : null}

                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2">Segments</Typography>
                          {drawerCanAddSegment ? (
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => multiClient.addSegmentForClient(drawerClientId)}
                            >
                              Add segment
                            </Button>
                          ) : null}
                        </Stack>
                        <Stack spacing={2}>
                          {(data.segments || []).map((segment, index) => {
                            const locked = drawerSegmentLocked(segment);
                            const expandKey = `${drawerClientId}-${index}`;
                            const expanded = Boolean(drawerExpandedSegments[expandKey]);
                            const showFull = !locked || expanded;
                            const verticalName = (data.verticalOptions || [])
                              .find((o) => String(o.value) === String(segment.vertical || ''))?.label || 'All';
                            const segLocationIds = segmentLocationIds(segment);
                            const locationNames = (data.locationOptions || [])
                              .filter((o) => segLocationIds.includes(o.value))
                              .map((o) => o.label);
                            const locationText = locationNames.length ? locationNames.join(', ') : 'All';
                            const summaryText = `${verticalName} · ${locationText} · ${formatCurrency(segment.effective_amount || segment.amount || 0)}`;
                            return (
                            <Paper
                              key={`${drawerClientId}-drawer-${segment.budget_id || index}`}
                              variant="outlined"
                              sx={{ p: 2, ...((!locked && drawerIsLiveMonth) ? { borderColor: 'primary.main' } : {}) }}
                            >
                              <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                  {locked ? (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                      <Chip size="small" color="success" variant="outlined" label="Live" />
                                      <Typography variant="body2" noWrap title={summaryText}>{summaryText}</Typography>
                                    </Stack>
                                  ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Typography variant="caption" color="text.secondary">
                                        Segment {index + 1}
                                      </Typography>
                                      {drawerIsLiveMonth ? (
                                        <Chip size="small" color="primary" variant="outlined" label="New" />
                                      ) : null}
                                    </Stack>
                                  )}
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    {locked ? (
                                      <Button
                                        size="small"
                                        onClick={() => setDrawerExpandedSegments((prev) => ({ ...prev, [expandKey]: !prev[expandKey] }))}
                                      >
                                        {expanded ? 'Hide' : 'Details'}
                                      </Button>
                                    ) : null}
                                    {!locked && (data.segments || []).length > 1 ? (
                                      <IconButton
                                        size="small"
                                        onClick={() => multiClient.deleteSegmentForClient(drawerClientId, index)}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    ) : null}
                                  </Stack>
                                </Stack>
                                {showFull ? (
                                  <>
                                <TextField
                                  size="small"
                                  select
                                  label="Vertical"
                                  disabled={locked}
                                  value={segment.vertical || ''}
                                  onChange={(e) => multiClient.updateSegmentForClient(drawerClientId, index, 'vertical', e.target.value)}
                                >
                                  <MenuItem value="">All</MenuItem>
                                  {(data.verticalOptions || []).map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                  ))}
                                </TextField>
                                <Autocomplete
                                  multiple
                                  size="small"
                                  disabled={locked}
                                  options={scopedLocationOptions(
                                    data.locationOptions || [],
                                    segment.vertical,
                                    segmentLocationIds(segment),
                                  )}
                                  getOptionLabel={(opt) => opt.label || ''}
                                  value={(data.locationOptions || []).filter((o) =>
                                    segmentLocationIds(segment).includes(o.value),
                                  )}
                                  onChange={(_, val) => {
                                    const ids = (val || []).map((item) => item.value);
                                    multiClient.updateSegmentForClient(drawerClientId, index, 'location_ids', ids);
                                    multiClient.updateSegmentForClient(drawerClientId, index, 'location', ids[0] || '');
                                  }}
                                  isOptionEqualToValue={(option, value) => option.value === value.value}
                                  renderInput={(params) => <TextField {...params} label="Locations" placeholder={segmentLocationIds(segment).length ? '' : 'All'} />}
                                />
                                <TextField
                                  size="small"
                                  label="Starting budget"
                                  disabled={locked}
                                  inputProps={{ inputMode: 'decimal' }}
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                  value={formatNumberInput(segment.amount)}
                                  onChange={(e) => multiClient.updateSegmentForClient(drawerClientId, index, 'amount', e.target.value.replace(/,/g, ''))}
                                />
                                <TextField
                                  size="small"
                                  label="Notes"
                                  multiline
                                  minRows={2}
                                  disabled={locked}
                                  value={segment.notes || ''}
                                  onChange={(e) => multiClient.updateSegmentForClient(drawerClientId, index, 'notes', e.target.value)}
                                />
                                  </>
                                ) : null}
                              </Stack>
                            </Paper>
                            );
                          })}
                        </Stack>
                      </Box>

                      {/* Change History */}
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                          <Box>
                            <Typography variant="subtitle2">Change History</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Recent budget changes for {formatMonthLabel(period)}.
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="text"
                            disabled={!(data.pageData?.history || []).length}
                            onClick={() => setDrawerHistoryOpen((prev) => !prev)}
                          >
                            {drawerHistoryOpen ? 'Hide' : 'Show'}
                          </Button>
                        </Stack>
                        {drawerHistoryOpen ? (
                          (data.pageData?.history || []).length ? (
                            <Stack spacing={1.5}>
                              {data.pageData.history.map((entry) => (
                                <Box
                                  key={entry.id}
                                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
                                >
                                  <Stack spacing={1}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                      <Box>
                                        <Typography variant="subtitle2">{entry.summary}</Typography>
                                        <Typography color="text.secondary" variant="caption">
                                          {(entry.actor?.name || entry.actor?.email || 'System')} • {formatDateTime(entry.created_at)}
                                        </Typography>
                                      </Box>
                                      {entry.entity_label ? (
                                        <Chip
                                          label={entry.entity_label}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            maxWidth: { sm: 320 },
                                            height: 'auto',
                                            flexShrink: 0,
                                            '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                                          }}
                                        />
                                      ) : null}
                                    </Stack>
                                    {entry.changed_fields?.length ? (
                                      <Typography color="text.secondary" variant="body2">
                                        Changed: {entry.changed_fields.join(', ')}
                                      </Typography>
                                    ) : null}
                                    {entry.changed_fields?.length && (entry.before || entry.after) ? (
                                      <Stack spacing={0.75}>
                                        {entry.changed_fields.map((field) => {
                                          const beforeValue = entry.before?.[field];
                                          const afterValue = entry.after?.[field];
                                          return (
                                            <Typography key={`${entry.id}-${field}`} variant="body2">
                                              <Box component="span" sx={{ fontWeight: 600 }}>{field}</Box>
                                              : {formatHistoryValue(beforeValue)} {' -> '} {formatHistoryValue(afterValue)}
                                            </Typography>
                                          );
                                        })}
                                      </Stack>
                                    ) : null}
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Alert severity="info">No budget change history yet for this month.</Alert>
                          )
                        ) : null}
                      </Box>

                      {/* Budget Discussion */}
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                          <Box>
                            <Typography variant="subtitle2">Budget Discussion</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Discuss budget changes with {isStaff ? 'the client' : 'Lead Gurus'} for {formatMonthLabel(period)}.
                            </Typography>
                          </Box>
                          {drawerComments.length ? (
                            <Chip label={`${drawerComments.length} comment${drawerComments.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                          ) : null}
                        </Stack>
                        {drawerComments.length ? (
                          <Stack spacing={1.5} sx={{ maxHeight: 320, overflow: 'auto', mb: 1.5 }}>
                            {drawerComments.map((comment) => (
                              <Box
                                key={comment.id}
                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
                              >
                                <Stack spacing={0.5}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2">
                                      {comment.author?.name || comment.author?.email || 'Unknown'}
                                    </Typography>
                                    <Typography color="text.secondary" variant="caption">
                                      {formatDateTime(comment.created_at)}
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {comment.body}
                                  </Typography>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>
                            No comments yet. Start the conversation below.
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                            placeholder="Add a comment..."
                            size="small"
                            value={drawerCommentText}
                            onChange={(e) => setDrawerCommentText(e.target.value)}
                          />
                          <Button
                            disabled={!drawerCommentText.trim() || drawerCommentSaving}
                            variant="contained"
                            onClick={submitDrawerComment}
                            sx={{ minWidth: 80, height: 56 }}
                          >
                            {drawerCommentSaving ? <CircularProgress size={20} /> : 'Post'}
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
                    justifyContent="flex-end"
                  >
                    {drawerHasDraftChanges ? (
                      <Button
                        variant="text"
                        color="error"
                        startIcon={<UndoOutlinedIcon />}
                        disabled={data.saving}
                        onClick={() => multiClient.discardForClient(drawerClientId).catch(() => {})}
                      >
                        Discard Changes
                      </Button>
                    ) : null}
                    {(!drawerIsActive
                      && (drawerIsPending ? (drawerHasDraftChanges || isStaff) : true))
                      || (drawerIsLiveMonth && drawerHasDraftChanges)
                      ? (
                      <Button
                        variant="outlined"
                        startIcon={<SaveOutlinedIcon />}
                        disabled={data.saving || !drawerHasDraftChanges}
                        onClick={() => multiClient.saveDraftForClient(drawerClientId).catch(() => {})}
                      >
                        {saveDraftLabel}
                      </Button>
                    ) : null}
                    {(!drawerIsActive && !drawerIsPending)
                      || (drawerIsLiveMonth && drawerHasDraftChanges) ? (
                      <Button
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon />}
                        disabled={data.saving}
                        onClick={() => guardScopeMix(
                          data.segments,
                          data.pageData,
                          () => multiClient.approveForClient(drawerClientId).catch(() => {})
                        )}
                      >
                        {drawerIsLiveMonth
                          ? (isStaff ? 'Approve Changes' : 'Submit Changes')
                          : (isStaff ? 'Approve' : 'Submit for Review')}
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              );
            })()}
          </Drawer>
        ) : null}

        {/* Single-client mode loading/empty states */}
        {!isMultiClientMode && loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : null}

        {!isMultiClientMode && !loading && !selectedClient ? (
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                <Typography color="text.secondary" variant="h6">
                  Select a client to get started
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Choose a client above to view and manage their monthly budget.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {!isMultiClientMode && !loading && selectedClient ? (
          <>
            <Card ref={walkthroughRefs.budgetTable} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  {/* ── Compact header: client · month · status ── */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
                      {!isStaff ? (
                        <Typography variant="subtitle1" fontWeight={700}>
                          {readOnlyClientName}
                        </Typography>
                      ) : null}
                      <Box ref={walkthroughRefs.monthNavigator}>
                        <MonthNavigator isStaff={isStaff} onChange={(newPeriod) => setPeriod(newPeriod)} value={period} />
                      </Box>
                      <Chip
                        color={getStatusChipColor(pageData?.status)}
                        label={formatStatusLabel(pageData?.status, { planned: isFutureMonth && String(pageData?.status || '').toLowerCase() === 'draft' })}
                        size="small"
                        variant="filled"
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Refresh data">
                        <IconButton size="small" onClick={loadBudgetCenter} disabled={loading}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        variant="text"
                        onClick={() => { setWalkthroughStep(0); setWalkthroughActive(true); }}
                      >
                        Tour
                      </Button>
                      {isPastMonth && !isStaff ? null : (
                        <Tooltip
                          title={!isStaff ? 'Most clients perform best with one flexible budget. Exact segment targets cannot be guaranteed due to the dynamic nature of Meta advertising.' : ''}
                          placement="bottom"
                        >
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            variant="outlined"
                            color={!isStaff && segments.length >= 1 ? 'inherit' : 'primary'}
                            onClick={() => {
                              // From the simple view, adding a segment needs the
                              // full editor (vertical/territory pickers live there).
                              if (simpleViewEligible && !showDetails) {
                                setShowDetails(true);
                              }
                              setSegments((prev) => [...prev, emptySegment(period)]);
                            }}
                          >
                            Add Segment
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>

                  {/* ── Inline summary bar ── */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 3 }}
                    divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
                    sx={{ px: 0.5, flexWrap: 'wrap' }}
                  >
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary">Budget</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{formatCurrency(currentSummary?.amount)}</Typography>
                      {monthDelta !== null && !segmentCompositionChanged ? (
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ color: monthDelta > 0 ? 'success.main' : monthDelta < 0 ? 'error.main' : 'text.secondary' }}
                        >
                          {monthDelta > 0 ? '+' : ''}{formatCurrency(monthDelta)}
                          {monthDeltaPct !== null ? ` (${monthDeltaPct > 0 ? '+' : ''}${monthDeltaPct.toFixed(0)}%)` : ''}
                        </Typography>
                      ) : null}
                    </Stack>
                    {showAdjustmentColumns
                      && Number(currentSummary?.effective_amount || 0) !== Number(currentSummary?.amount || 0) ? (
                      <Stack direction="row" alignItems="baseline" spacing={0.75}>
                        <Typography variant="subtitle2" color="text.secondary">Effective</Typography>
                        <Typography variant="subtitle2" fontWeight={700}>{formatCurrency(currentSummary?.effective_amount || currentSummary?.amount)}</Typography>
                      </Stack>
                    ) : null}
                    {!simpleViewEligible ? (
                      <Stack direction="row" alignItems="baseline" spacing={0.75}>
                        <Typography variant="subtitle2" color="text.secondary">Segments</Typography>
                        <Typography variant="subtitle2" fontWeight={700}>{currentSummary?.segment_count || segments.length || 0}</Typography>
                      </Stack>
                    ) : null}
                    {previousAmount && (pageData?.seeded_from_previous || !segmentCompositionChanged) ? (
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        {pageData?.seeded_from_previous
                          ? `Copied from ${formatMonthLabel(pageData?.seed_source_period)}`
                          : `vs ${formatCurrency(previousAmount)} last month`}
                      </Typography>
                    ) : null}
                  </Stack>

                  <Divider />

                  {/* Optimization guidance — discourage unnecessary segments */}
                  {!isStaff && !isLiveMonth ? (
                    <Box
                      sx={{
                        bgcolor: segments.length > 1 ? 'warning.50' : 'action.hover',
                        border: segments.length > 1 ? '1px solid' : 'none',
                        borderColor: 'warning.200',
                        borderRadius: 1.5,
                        px: 2,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {segments.length > 1 ? (
                          <>
                            <strong>Tip:</strong> You have {segments.length} segments.
                            Fewer segments give Lead Gurus more flexibility to allocate your spend
                            where it performs best, lowering your overall cost of marketing. Most clients see the
                            best results with a single flexible budget.
                            Due to the dynamic nature of Meta advertising, exact budget segment targets are best-effort
                            and cannot be guaranteed.
                          </>
                        ) : (
                          <>
                            <strong>Recommended:</strong> One flexible budget lets Lead Gurus use real-time
                            data to optimize your campaigns and lower your cost of marketing. Only add
                            segments if you need hard caps by vertical or market.
                          </>
                        )}
                      </Typography>
                    </Box>
                  ) : null}

                  {/* Welcome banner for new clients with no budget history */}
                  {!isStaff && !pageData?.seeded_from_previous && !pageData?.previous_budget
                    && (!Array.isArray(pageData?.segments) || pageData.segments.length === 0) ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Welcome to Budget Center</Typography>
                      <Typography variant="body2">
                        Set your monthly ad-spend budget below. Most clients perform best with a single flexible budget —
                        just enter your total amount and submit. Lead Gurus will review and activate it before the month begins.
                      </Typography>
                    </Alert>
                  ) : null}

                  {/* Quick submit shortcut for unchanged seeded budgets */}
                  {!isStaff && !isLiveMonth && segmentsUnchangedFromLoad
                    && approvalTotal > 0 && !progressSteps.submitted && pageData?.seeded_from_previous ? (
                    <Alert
                      severity="success"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          disabled={saving}
                          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                          onClick={openApproveDialog}
                        >
                          Looks Good — Submit
                        </Button>
                      }
                    >
                      Same budget as {formatMonthLabel(pageData?.seed_source_period)}. Submit as-is or adjust below.
                    </Alert>
                  ) : null}

                  {simpleViewEligible && showDetails ? (
                    <Box sx={{ mb: 0.5 }}>
                      <Button size="small" onClick={() => setShowDetails(false)}>
                        ← Back to simple view
                      </Button>
                    </Box>
                  ) : null}

                  {/* Allocation breakdown — answers "where is my money going"
                      at a glance for multi-segment clients */}
                  {!(simpleViewEligible && !showDetails) && segments.length >= 2 ? (
                    <AllocationBreakdown
                      segments={segments}
                      verticalOptions={verticalOptions}
                      locationOptions={locationOptions}
                      useEffective={showAdjustmentColumns}
                    />
                  ) : null}

                  {simpleViewEligible && !showDetails ? (
                    <Stack spacing={1.5}>
                      {segments.map((segment, index) => {
                        if (isLiveMonth) {
                          return (
                            <SimpleBudgetCard
                              key={`simple-${segment.budget_id || index}`}
                              segment={segment}
                              period={period}
                              verticalOptions={verticalOptions}
                              locationOptions={locationOptions}
                              onRequestChange={() => { setSelectedSegmentIndex(index); setAdjustmentsDialogOpen(true); }}
                              onTurnOff={segment.budget_id ? () => openTurnOffDialog(segment) : null}
                            />
                          );
                        }
                        const rowStatus = getBudgetCenterRowStatus(segment, {
                          isLiveMonth,
                          liveBudgetIds: pageData?.live_budget_ids,
                        });
                        return (
                          <SimplePlanCard
                            key={`simple-${segment.budget_id || index}`}
                            segment={segment}
                            index={index}
                            verticalOptions={verticalOptions}
                            locationOptions={locationOptions}
                            rowStatus={rowStatus}
                            isReadOnly={isReadOnly}
                            isFutureMonth={isFutureMonth}
                            isCurrentMonth={isCurrentMonth}
                            updateSegment={updateSegment}
                            previousAmount={getPreviousSegmentAmount(segment)}
                            rowError={rowErrors[index]}
                          />
                        );
                      })}
                      <Box>
                        <Button size="small" onClick={() => setShowDetails(true)}>
                          See full details
                        </Button>
                      </Box>
                    </Stack>
                  ) : isMobile ? (
                    <Stack ref={segmentsTableRef} spacing={1.5}>
                      {segments.map((segment, index) => {
                        const rowStatus = getBudgetCenterRowStatus(segment, {
                          isLiveMonth,
                          liveBudgetIds: pageData?.live_budget_ids,
                        });
                        return (
                          <MobileSegmentCard
                            key={`${segment.budget_id || 'new'}-${index}`}
                            segment={segment}
                            index={index}
                            rowErrors={rowErrors[index]}
                            isLiveMonth={isLiveMonth}
                            isCurrentMonth={isCurrentMonth}
                            liveOrigin={isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)}
                            showAdjustmentColumns={showAdjustmentColumns}
                            verticalOptions={verticalOptions}
                            locationOptions={locationOptions}
                            updateSegment={updateSegment}
                            onNotes={openNotesDialog}
                            onDuplicate={duplicateSegment}
                            onAdjustment={(i) => { setSelectedSegmentIndex(i); setAdjustmentsDialogOpen(true); }}
                            onTurnOff={openTurnOffDialog}
                            onTurnOn={turnOnSegment}
                            isStaff={isStaff}
                            onDelete={setDeleteRowIndex}
                            canDelete={segments.length > 1}
                            rowStatus={rowStatus}
                            isFutureMonth={isFutureMonth}
                            optionSet={optionSet}
                            previousAmount={getPreviousSegmentAmount(segment)}
                            isReadOnly={isReadOnly}
                          />
                        );
                      })}
                    </Stack>
                  ) : (
                    <TableContainer
                      ref={segmentsTableRef}
                      component={Paper}
                      variant="outlined"
                      sx={{ borderRadius: 2, overflow: 'auto', maxHeight: '65vh' }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow
                            sx={{
                              bgcolor: 'var(--mui-palette-background-level1)',
                              '& th': {
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                bgcolor: 'var(--mui-palette-background-level1)',
                              },
                            }}
                          >
                            <TableCell>Vertical</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Starting Budget</TableCell>
                            {showAdjustmentColumns ? <TableCell>Adjustments</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Effective Budget</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>MTD Spend</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Remaining</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Remaining/Day</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Pacing</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Projected</TableCell> : null}
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {segments.map((segment, index) => {
                            const rowStatus = getBudgetCenterRowStatus(segment, {
                              isLiveMonth,
                              liveBudgetIds: pageData?.live_budget_ids,
                            });
                            const singleRemaining = Number(segment.effective_amount || segment.amount || 0) - Number(segment.month_to_date_spend || 0);
                            const launch = getSegmentLaunch(segment);
                            // Before a funded launch the $0 effective budget is
                            // money that hasn't started — never "overspent".
                            const isScheduledLaunch = Boolean(launch?.pacingNote) && launch.phase === 'scheduled';
                            const isOverspent = showAdjustmentColumns && singleRemaining < 0 && !isScheduledLaunch;
                            const rowLiveOrigin = isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids);
                            // Rows are pre-sorted by vertical, so a subheader
                            // starts wherever the vertical changes. Count the
                            // contiguous run (not the whole-table total) so an
                            // in-progress edit that splits a vertical still
                            // labels each run honestly.
                            const verticalKey = String(segment.vertical || '');
                            const startsVerticalGroup = showVerticalGroupHeaders
                              && (index === 0 || String(segments[index - 1].vertical || '') !== verticalKey);
                            let verticalGroupCount = 0;
                            if (startsVerticalGroup) {
                              while (index + verticalGroupCount < segments.length
                                && String(segments[index + verticalGroupCount].vertical || '') === verticalKey) {
                                verticalGroupCount += 1;
                              }
                            }
                            const verticalGroupLabel = verticalOptions.find(
                              (option) => String(option.value) === verticalKey
                            )?.label || segment.vertical_data?.vertical || 'All verticals';
                            return (
                            <React.Fragment key={`${segment.budget_id || 'new'}-${index}`}>
                            {startsVerticalGroup ? (
                              <TableRow>
                                <TableCell
                                  colSpan={showAdjustmentColumns ? 12 : 5}
                                  sx={{
                                    py: 0.5,
                                    bgcolor: 'var(--mui-palette-background-level1)',
                                  }}
                                >
                                  <Stack direction="row" spacing={1} alignItems="baseline">
                                    <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.8 }}>
                                      {verticalGroupLabel}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {verticalGroupCount} segment{verticalGroupCount === 1 ? '' : 's'}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ) : null}
                            <TableRow
                              hover
                              sx={{
                                '& td': {
                                  py: 1,
                                  verticalAlign: 'top',
                                },
                                ...(isOverspent ? { bgcolor: 'rgba(211, 47, 47, 0.06)', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.12) !important' } } : {}),
                              }}
                            >
                              <TableCell sx={{ minWidth: 180 }}>
                                <Stack spacing={0.75}>
                                  <TextField
                                    disabled={isReadOnly || (isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids))}
                                    error={Boolean(rowErrors[index]?.segment)}
                                    fullWidth
                                    select
                                    size="small"
                                    value={segment.vertical}
                                    onChange={(event) => updateSegment(index, 'vertical', event.target.value)}
                                  >
                                    <MenuItem value="">All</MenuItem>
                                    {verticalOptions.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  {!segment.vertical && !segment.location ? (
                                    <Chip label="Flexible" size="small" sx={{ alignSelf: 'flex-start' }} variant="soft" />
                                  ) : null}
                                  {rowErrors[index]?.segment ? (
                                    <Typography color="error" variant="caption">
                                      {rowErrors[index].segment}
                                    </Typography>
                                  ) : null}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ minWidth: 200 }}>
                                <Autocomplete
                                  multiple
                                  size="small"
                                  disabled={isReadOnly || (isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids))}
                                  options={scopedLocationOptions(
                                    locationOptions,
                                    segment.vertical,
                                    segmentLocationIds(segment),
                                  )}
                                  getOptionLabel={(opt) => opt.label || ''}
                                  value={locationOptions.filter((o) => segmentLocationIds(segment).includes(o.value))}
                                  onChange={(_, val) => {
                                    const ids = (val || []).map((item) => item.value);
                                    updateSegment(index, 'location_ids', ids);
                                    updateSegment(index, 'location', ids[0] || '');
                                  }}
                                  isOptionEqualToValue={(option, value) => option.value === value.value}
                                  renderInput={(params) => <TextField {...params} placeholder={segmentLocationIds(segment).length ? '' : 'All'} />}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 140 }}>
                                <TextField
                                  disabled={isReadOnly || (isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids))}
                                  error={Boolean(rowErrors[index]?.amount)}
                                  fullWidth
                                  helperText={rowErrors[index]?.amount || ''}
                                  size="small"
                                  inputProps={{ inputMode: 'decimal' }}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                  }}
                                  value={formatNumberInput(segment.amount)}
                                  onChange={(event) => updateSegment(index, 'amount', event.target.value.replace(/,/g, ''))}
                                />
                                {!isReadOnly && !rowLiveOrigin ? (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Starts on (optional)"
                                    helperText={launch?.caption || ''}
                                    value={segment.starts_on || ''}
                                    onChange={(event) => updateSegment(index, 'starts_on', event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                      min: dayjs(period).startOf('month').format('YYYY-MM-DD'),
                                      max: dayjs(period).endOf('month').format('YYYY-MM-DD'),
                                    }}
                                    sx={{ mt: 1 }}
                                  />
                                ) : launch ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {launch.caption}
                                  </Typography>
                                ) : null}
                                {isCurrentMonth && !showAdjustmentColumns
                                  && Number(segment.month_to_date_spend || 0) > 0
                                  && !rowErrors[index]?.amount ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      min {formatCurrency(segment.month_to_date_spend)} — already spent this month
                                    </Typography>
                                  ) : null}
                                {(() => {
                                  const prevAmt = getPreviousSegmentAmount(segment);
                                  if (prevAmt === null || prevAmt === 0) return null;
                                  const delta = Number(segment.amount || 0) - prevAmt;
                                  if (delta === 0) return (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                      Same as last month
                                    </Typography>
                                  );
                                  return (
                                    <Typography
                                      variant="caption"
                                      sx={{ mt: 0.5, color: delta > 0 ? 'success.main' : 'error.main' }}
                                    >
                                      was {formatCurrency(prevAmt)} last month
                                    </Typography>
                                  );
                                })()}
                              </TableCell>
                              {showAdjustmentColumns ? (
                                <TableCell>{formatCurrency(segment.adjustment_total)}</TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell>{formatCurrency(segment.effective_amount || segment.amount)}</TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell>{formatCurrency(segment.month_to_date_spend)}</TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell sx={isOverspent ? { color: 'error.main', fontWeight: 600 } : undefined}>
                                  {formatCurrency(singleRemaining)}
                                </TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell sx={!segment.is_turned_off && isOverspent ? { color: 'error.main', fontWeight: 600 } : undefined}>{segment.is_turned_off || isScheduledLaunch ? '—' : formatCurrency(segment.remaining_per_day)}</TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell>
                                  <PacingCell effectiveAmount={segment.effective_amount || segment.amount} monthToDateSpend={segment.month_to_date_spend} pacingPercentage={segment.pacing_percentage} isTurnedOff={segment.is_turned_off} launchNote={launch?.pacingNote} />
                                </TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell>{launch?.pacingNote ? '—' : formatCurrency(segment.projected_spend)}</TableCell>
                              ) : null}
                              <TableCell>
                                <Chip
                                  color={getStatusChipColor(rowStatus)}
                                  label={formatStatusLabel(rowStatus, {
                                    planned: isFutureMonth && String(rowStatus || '').toLowerCase() === 'draft',
                                  })}
                                  size="small"
                                  variant="filled"
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Button
                                  size="small"
                                  onClick={() => openNotesDialog(index)}
                                >
                                  {segment.notes ? 'Edit Notes' : 'Notes'}
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => duplicateSegment(index)}
                                >
                                  Duplicate
                                </Button>
                                {showAdjustmentColumns ? (
                                  <Button
                                    color="primary"
                                    size="small"
                                    startIcon={<EditOutlinedIcon />}
                                    sx={{ mx: 0.5 }}
                                    variant="contained"
                                    onClick={() => {
                                      setSelectedSegmentIndex(index);
                                      setAdjustmentsDialogOpen(true);
                                    }}
                                  >
                                    Add Adjustment
                                  </Button>
                                ) : null}
                                {isLiveMonth ? (
                                  isStaff && segment.is_turned_off ? (
                                    <Button
                                      color="success"
                                      size="small"
                                      onClick={() => turnOnSegment(segment)}
                                    >
                                      Turn Back On
                                    </Button>
                                  ) : segment.has_pending_turn_off && !isStaff ? (
                                    <Chip label={turnOffChipLabel(segment)} size="small" color="warning" variant="outlined" />
                                  ) : segment.budget_id && !segment.is_turned_off ? (
                                    <Button
                                      size="small"
                                      onClick={() => openTurnOffDialog(segment)}
                                    >
                                      Turn Off
                                    </Button>
                                  ) : null
                                ) : null}
                                <Tooltip title={segments.length === 1 ? 'At least one segment is required' : (isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)) ? 'Live segments cannot be deleted' : 'Remove segment'}>
                                  <span>
                                    <IconButton
                                      onClick={() => setDeleteRowIndex(index)}
                                      disabled={(isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)) || segments.length === 1}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                            </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card
              ref={walkthroughRefs.actionBar}
              variant="outlined"
              sx={{
                borderRadius: 3,
                position: 'sticky',
                bottom: 16,
                zIndex: 5,
                overflow: 'visible',
                boxShadow: 3,
              }}
            >
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                  <Stack spacing={0.5}>
                    <Stack alignItems={{ xs: 'flex-start', sm: 'center' }} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Typography variant="h6">
                        {formatCurrency(showAdjustmentColumns && effectiveTotal !== approvalTotal ? effectiveTotal : approvalTotal)}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {showAdjustmentColumns && effectiveTotal !== approvalTotal
                          ? `effective · ${formatCurrency(approvalTotal)} starting`
                          : 'total budget'}
                      </Typography>
                    </Stack>
                    <Typography color="text.secondary" variant="body2">
                      {allSegmentsOff
                        ? 'All segments are off. Add new segments or increase existing ones with adjustments.'
                        : isLiveMonth && hasDraftChanges
                          ? (isStaff ? 'Review pending changes and approve when ready.' : 'You have pending changes. Submit when ready.')
                          : isLiveMonth
                            ? 'Starting budgets are locked. Use Add Adjustment on any row to make in-month changes.'
                            : isApprovedPendingLive
                              ? (isStaff ? 'This budget is approved and ready to go live.' : 'Your budget is approved and will be made live by Lead Gurus.')
                              : isPendingApproval
                                ? (isStaff ? 'Client submitted this budget. Review and approve, then mark it live.' : 'Your budget has been submitted and is under review.')
                                : isStaff
                                  ? 'Set starting budgets and approve when ready.'
                                  : 'Set your budget amounts, then submit for review.'}
                    </Typography>
                    {pageData?.activity_summary?.saved_draft ? (
                      <Typography color="text.secondary" variant="caption">
                        Saved by {formatActorLabel(pageData.activity_summary.saved_draft)} · {formatDateTime(pageData.activity_summary.saved_draft.created_at)}
                      </Typography>
                    ) : null}
                    {pageData?.activity_summary?.submitted ? (
                      <Typography color="text.secondary" variant="caption">
                        Submitted by {formatActorLabel(pageData.activity_summary.submitted)} · {formatDateTime(pageData.activity_summary.submitted.created_at)}
                      </Typography>
                    ) : null}
                    {pageData?.activity_summary?.approved ? (
                      <Typography color="text.secondary" variant="caption">
                        Approved by {formatActorLabel(pageData.activity_summary.approved)} · {formatDateTime(pageData.activity_summary.approved.created_at)}
                      </Typography>
                    ) : null}
                    {pageData?.activity_summary?.marked_live ? (
                      <Typography color="text.secondary" variant="caption">
                        Made live by {formatActorLabel(pageData.activity_summary.marked_live)} · {formatDateTime(pageData.activity_summary.marked_live.created_at)}
                      </Typography>
                    ) : null}
                    {comments.length > 0 ? (
                      <Chip
                        icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
                        label={`${comments.length} comment${comments.length !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => walkthroughRefs.discussion.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        sx={{ alignSelf: 'flex-start', cursor: 'pointer' }}
                      />
                    ) : null}
                  </Stack>

                  {isPastMonth && !isStaff ? (
                    <Chip label="View Only — Past Month" variant="outlined" />
                  ) : (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{ alignItems: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
                  >
                    {/* Staff: Mark Live — primary action when budget is approved */}
                    {isStaff && isApprovedPendingLive ? (
                      <Button
                        color="success"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon />}
                        variant="contained"
                        onClick={() => setMarkLiveDialogOpen(true)}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
                      >
                        Mark Live
                      </Button>
                    ) : null}

                    {/* Client: submitted state — no action buttons, show status chip */}
                    {!isStaff && (isApprovedPendingLive || isPendingApproval) && !hasDraftChanges ? (
                      <Chip
                        color={isApprovedPendingLive ? 'success' : 'info'}
                        label={isApprovedPendingLive ? 'Approved — going live soon' : 'Submitted — under review'}
                        variant="outlined"
                      />
                    ) : null}

                    {/* Primary submit/approve button — shown when there are actions to take */}
                    {(!isApprovedPendingLive || hasDraftChanges) && (!isPendingApproval || hasDraftChanges || isStaff) && (!isLiveMonth || hasDraftChanges) ? (
                      <Button
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon />}
                        variant="contained"
                        onClick={openApproveDialog}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 160 }}
                      >
                        {approvalActionLabel}
                      </Button>
                    ) : null}

                    {/* Secondary save button */}
                    {(!isApprovedPendingLive || hasDraftChanges) && (!isPendingApproval || hasDraftChanges || isStaff) && (!isLiveMonth || hasDraftChanges) ? (
                      <Button
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} /> : <SaveOutlinedIcon />}
                        variant="outlined"
                        onClick={saveDraft}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
                      >
                        {saveDraftLabel}
                      </Button>
                    ) : null}

                    {/* Discard — only when there's an unsubmitted draft to discard */}
                    {pageData?.has_unsubmitted_draft ? (
                      <Tooltip title="Revert to the last saved version">
                        <span>
                          <Button
                            color="error"
                            disabled={saving}
                            startIcon={<UndoOutlinedIcon />}
                            variant="text"
                            onClick={() => setDiscardDialogOpen(true)}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                          >
                            Discard Changes
                          </Button>
                        </span>
                      </Tooltip>
                    ) : null}
                  </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography variant="h6">Change History</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Recent budget changes for this client and month.
                      </Typography>
                    </Box>
                    <Button
                      disabled={!(pageData?.history || []).length}
                      onClick={() => setHistoryOpen((prev) => !prev)}
                      variant="text"
                    >
                      {historyOpen ? 'Hide History' : 'Show History'}
                    </Button>
                  </Stack>

                  {historyOpen ? (
                    (pageData?.history || []).length ? (
                      <Stack spacing={1.5}>
                        {pageData.history.map((entry) => (
                          <Box
                            key={entry.id}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              p: 2,
                            }}
                          >
                            <Stack spacing={1}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Box>
                                  <Typography variant="subtitle2">
                                    {entry.summary}
                                  </Typography>
                                  <Typography color="text.secondary" variant="caption">
                                    {(entry.actor?.name || entry.actor?.email || 'System')} • {formatDateTime(entry.created_at)}
                                  </Typography>
                                </Box>
                                {entry.entity_label ? (
                                  <Chip
                                    label={entry.entity_label}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      maxWidth: { sm: 320 },
                                      height: 'auto',
                                      flexShrink: 0,
                                      '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                                    }}
                                  />
                                ) : null}
                              </Stack>

                              {entry.changed_fields?.length ? (
                                <Typography color="text.secondary" variant="body2">
                                  Changed: {entry.changed_fields.join(', ')}
                                </Typography>
                              ) : null}

                              {entry.changed_fields?.length && (entry.before || entry.after) ? (
                                <Stack spacing={0.75}>
                                  {entry.changed_fields.map((field) => {
                                    const beforeValue = entry.before?.[field];
                                    const afterValue = entry.after?.[field];
                                    return (
                                      <Typography key={`${entry.id}-${field}`} variant="body2">
                                        <Box component="span" sx={{ fontWeight: 600 }}>
                                          {field}
                                        </Box>
                                        : {formatHistoryValue(beforeValue)} {' -> '} {formatHistoryValue(afterValue)}
                                      </Typography>
                                    );
                                  })}
                                </Stack>
                              ) : null}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info">No budget change history yet for this month.</Alert>
                    )
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            {/* Adjustments and Notes dialogs moved outside !isMultiClientMode block */}

            <Dialog fullWidth maxWidth="xs" onClose={() => setZeroAmountWarningOpen(false)} open={zeroAmountWarningOpen}>
              <DialogTitle>$0 Segments Detected</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Alert severity="warning">
                    {segments.filter((s) => Number(s.amount) === 0).length} segment{segments.filter((s) => Number(s.amount) === 0).length > 1 ? 's have' : ' has'} a $0 budget and will not receive any traffic.
                  </Alert>
                  <Typography color="text.secondary" variant="body2">
                    You can continue or go back to adjust the amounts.
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setZeroAmountWarningOpen(false)}>Go Back</Button>
                <Button
                  variant="contained"
                  onClick={() => { setZeroAmountWarningOpen(false); setApproveDialogOpen(true); }}
                >
                  Continue Anyway
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog fullWidth maxWidth="sm" onClose={closeApproveDialog} open={approveDialogOpen}>
              <DialogTitle>{approvalActionLabel}</DialogTitle>
              <DialogContent>
                {(() => {
                  const submittingPayload = buildPayload();
                  const submittingSegments = submittingPayload.segments || [];
                  const submittingTotal = submittingSegments.reduce((sum, s) => sum + Number(s.amount || 0), 0);
                  const getLabel = (id, options) => {
                    if (!id) return 'All';
                    const match = options.find((o) => String(o.value) === String(id));
                    return match?.label || String(id);
                  };
                  return (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                      <Typography color="text.secondary" variant="body2">
                        {formatMonthLabel(period)} · {readOnlyClientName || 'this client'}
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 600, py: 0.75 } }}>
                            <TableCell>Vertical</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {submittingSegments.map((seg, i) => (
                            <TableRow key={i} sx={{ '& td': { py: 0.75 } }}>
                              <TableCell>{getLabel(seg.vertical, verticalOptions)}</TableCell>
                              <TableCell>{getLabel(seg.location, locationOptions)}</TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={600}>{formatCurrency(seg.amount)}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ '& td': { borderBottom: 'none', pt: 1.5 } }}>
                            <TableCell colSpan={2}>
                              <Typography fontWeight={700}>
                                {isLiveMonth ? 'Changes Total' : 'Total'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={700}>{formatCurrency(submittingTotal)}</Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <Alert severity="info">
                        {isStaff
                          ? (isLiveMonth
                            ? 'These changes will be approved. You will still need to mark them live.'
                            : 'This will approve the budget. You will still need to mark it live.')
                          : (isLiveMonth
                            ? 'Your changes will be submitted for review by Lead Gurus before going live.'
                            : 'Your budget will be submitted for review by Lead Gurus before going live.')}
                      </Alert>
                    </Stack>
                  );
                })()}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeApproveDialog}>Cancel</Button>
                <Button
                  onClick={confirmApproveDraft}
                  startIcon={<CheckCircleOutlineIcon />}
                  variant="contained"
                >
                  {isStaff
                    ? (isLiveMonth ? 'Confirm Approval' : 'Confirm Approval')
                    : (isLiveMonth ? 'Submit Changes' : 'Submit for Review')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Turn Off dialog moved outside !isMultiClientMode block */}

            {/* Mark Live confirmation */}
            <Dialog fullWidth maxWidth="xs" onClose={() => setMarkLiveDialogOpen(false)} open={markLiveDialogOpen}>
              <DialogTitle>Mark Budget Live</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Typography variant="body2">
                    This will activate the {formatMonthLabel(period)} budget for {readOnlyClientName || 'this client'}. The client will be notified.
                  </Typography>
                  <Alert severity="warning">
                    This action cannot be undone. The budget will immediately become active.
                  </Alert>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setMarkLiveDialogOpen(false)}>Cancel</Button>
                <Button color="success" onClick={confirmMarkLive} variant="contained">
                  Confirm — Mark Live
                </Button>
              </DialogActions>
            </Dialog>

            {/* Discard confirmation */}
            <Dialog fullWidth maxWidth="xs" onClose={() => setDiscardDialogOpen(false)} open={discardDialogOpen}>
              <DialogTitle>Discard Changes</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Typography variant="body2">
                    This will revert to the last saved version. Any unsaved edits will be lost.
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDiscardDialogOpen(false)}>Cancel</Button>
                <Button color="error" onClick={confirmDiscard} variant="contained">
                  Discard
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete row confirmation */}
            <Dialog fullWidth maxWidth="xs" onClose={() => setDeleteRowIndex(null)} open={deleteRowIndex !== null}>
              <DialogTitle>Remove Segment</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Typography variant="body2">
                    Remove this segment from the table? This only takes effect when you save or submit.
                  </Typography>
                  {deleteRowIndex !== null && segments[deleteRowIndex] ? (
                    <Chip
                      label={buildSegmentLabel(segments[deleteRowIndex], optionSet)}
                      variant="outlined"
                    />
                  ) : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteRowIndex(null)}>Cancel</Button>
                <Button color="error" onClick={confirmDeleteRow} variant="contained">
                  Remove
                </Button>
              </DialogActions>
            </Dialog>

            {/* Copy from month dialog */}
            <Dialog fullWidth maxWidth="xs" onClose={() => setCopyMonthDialogOpen(false)} open={copyMonthDialogOpen}>
              <DialogTitle>Copy Budget From Another Month</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Typography color="text.secondary" variant="body2">
                    Select a month to copy its budget segments into {formatMonthLabel(period)}.
                  </Typography>
                  <TextField
                    fullWidth
                    label="Source Month"
                    select
                    value={copySourcePeriod}
                    onChange={(e) => setCopySourcePeriod(e.target.value)}
                  >
                    {availableMonths.map((m) => (
                      <MenuItem key={m.period} value={m.period}>{m.label}</MenuItem>
                    ))}
                  </TextField>
                  {!availableMonths.length ? (
                    <Alert severity="info">No months with live or approved budgets found for this client.</Alert>
                  ) : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCopyMonthDialogOpen(false)}>Cancel</Button>
                <Button disabled={!copySourcePeriod} onClick={confirmCopyFromMonth} variant="contained">
                  Copy Segments
                </Button>
              </DialogActions>
            </Dialog>

            {/* Budget Discussion */}
            <Card ref={walkthroughRefs.discussion} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">Budget Discussion</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Discuss budget changes with {isStaff ? 'the client' : 'Lead Gurus'} for this month.
                      </Typography>
                    </Box>
                    {comments.length ? (
                      <Chip label={`${comments.length} comment${comments.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                    ) : null}
                  </Stack>

                  {comments.length ? (
                    <Stack spacing={1.5} sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {comments.map((comment) => (
                        <Box
                          key={comment.id}
                          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
                        >
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2">
                                {comment.author?.name || comment.author?.email || 'Unknown'}
                              </Typography>
                              <Typography color="text.secondary" variant="caption">
                                {formatDateTime(comment.created_at)}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {comment.body}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      No comments yet. Start the conversation below.
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      placeholder="Add a comment..."
                      size="small"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                    />
                    <Button
                      disabled={!commentText.trim() || commentSaving}
                      variant="contained"
                      onClick={submitComment}
                      sx={{ minWidth: 100, height: 56 }}
                    >
                      {commentSaving ? <CircularProgress size={20} /> : 'Post'}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </>
        ) : null}
      </Stack>

      {/* Shared dialogs — rendered outside single/multi client conditionals */}
      <Dialog fullWidth maxWidth="md" onClose={() => { setAdjustmentsDialogOpen(false); setAdjustmentError(''); setMcDialogClientId(null); setAdjustmentMode('total'); }} open={adjustmentsDialogOpen}>
        <DialogTitle>Segment Adjustments</DialogTitle>
        <DialogContent>
          {dialogSegment ? (() => {
            const startingAmt = Number(dialogSegment.amount || 0);
            const adjTotal = Number(dialogSegment.adjustment_total || 0);
            const currentEffective = Number(dialogSegment.effective_amount || dialogSegment.amount || 0);
            const pendingDeltaRaw = dialogSegment.pendingAdjustment?.amount_delta;
            const pendingDelta = Number(pendingDeltaRaw || 0);
            const hasPendingDelta = pendingDeltaRaw !== '' && pendingDeltaRaw !== null && pendingDeltaRaw !== undefined && !Number.isNaN(pendingDelta);
            const newEffective = currentEffective + (hasPendingDelta ? pendingDelta : 0);
            const newTotalFieldValue = hasPendingDelta
              ? String(Number((currentEffective + pendingDelta).toFixed(2)))
              : '';
            const setPendingDelta = (value) => {
              if (mcDialogClientId) {
                multiClient.updatePendingAdjustmentForClient(mcDialogClientId, selectedSegmentIndex, 'amount_delta', value);
              } else {
                updatePendingAdjustment(selectedSegmentIndex, 'amount_delta', value);
              }
            };
            const handleNewTotalChange = (value) => {
              if (value === '' || value === '-') {
                setPendingDelta('');
                return;
              }
              const parsed = Number(value);
              if (Number.isNaN(parsed)) {
                setPendingDelta('');
                return;
              }
              const nextDelta = Number((parsed - currentEffective).toFixed(2));
              setPendingDelta(String(nextDelta));
            };
            return (
              <Stack spacing={2} sx={{ pt: 1 }}>
                {adjustmentError ? (
                  <Alert severity="error" onClose={() => setAdjustmentError('')}>
                    {adjustmentError}
                  </Alert>
                ) : null}

                <Typography variant="subtitle2">
                  {buildSegmentLabel(dialogSegment, dialogOptionSet)}
                </Typography>

                <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2 }}>
                  <Typography color="text.secondary" variant="caption">
                    Current effective budget
                  </Typography>
                  <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} variant="h5">
                    {formatCurrency(currentEffective)}
                  </Typography>
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
                    Starting {formatCurrency(startingAmt)}
                    {adjTotal !== 0 ? (
                      <>
                        {' · Adjustments '}
                        {adjTotal > 0 ? '+' : ''}
                        {formatCurrency(adjTotal)}
                      </>
                    ) : null}
                  </Typography>
                </Box>

                {(dialogSegment.adjustments || []).length ? (
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="caption">
                      Existing adjustments
                    </Typography>
                    {(dialogSegment.adjustments || []).map((adjustment) => (
                      <Box key={adjustment.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                          <Box>
                            <Typography variant="body1">
                              {Number(adjustment.amount_delta) > 0 ? '+' : ''}
                              {formatCurrency(adjustment.amount_delta)}
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                              Effective {dayjs(adjustment.effective_date).format('MMM D, YYYY')}
                            </Typography>
                            {adjustment.notes ? (
                              <Typography color="text.secondary" variant="body2">
                                {adjustment.notes}
                              </Typography>
                            ) : null}
                          </Box>
                          <Button variant="text" disabled={saving} onClick={() => deleteAdjustment(adjustment.id)}>
                            Remove
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : null}

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Add an adjustment
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Pick how you want to enter the change — we&apos;ll store the difference, not a replacement amount.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => setAdjustmentMode('total')}
                    size="small"
                    variant={adjustmentMode === 'total' ? 'contained' : 'outlined'}
                  >
                    Set new total
                  </Button>
                  <Button
                    onClick={() => setAdjustmentMode('delta')}
                    size="small"
                    variant={adjustmentMode === 'delta' ? 'contained' : 'outlined'}
                  >
                    Change by amount (+/−)
                  </Button>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    {adjustmentMode === 'delta' ? (
                      <TextField
                        fullWidth
                        helperText="Enter a positive number to add, or a negative (−) to subtract."
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        label="Change by"
                        placeholder="e.g. 500 or -500"
                        type="number"
                        value={pendingDeltaRaw || ''}
                        onChange={(event) => setPendingDelta(event.target.value)}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        helperText={`Current is ${formatCurrency(currentEffective)}. We'll compute the change for you.`}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        label="New effective total"
                        type="number"
                        value={newTotalFieldValue}
                        onChange={(event) => handleNewTotalChange(event.target.value)}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Effective Date"
                      type="date"
                      value={dialogSegment.pendingAdjustment?.effective_date || getDefaultAdjustmentDate(period)}
                      inputProps={{
                        min: dayjs(period).startOf('month').format('YYYY-MM-DD'),
                        max: dayjs(period).endOf('month').format('YYYY-MM-DD'),
                      }}
                      onChange={(event) => {
                        if (mcDialogClientId) {
                          multiClient.updatePendingAdjustmentForClient(mcDialogClientId, selectedSegmentIndex, 'effective_date', event.target.value);
                        } else {
                          updatePendingAdjustment(selectedSegmentIndex, 'effective_date', event.target.value);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={dialogSegment.pendingAdjustment?.notes || ''}
                      onChange={(event) => {
                        if (mcDialogClientId) {
                          multiClient.updatePendingAdjustmentForClient(mcDialogClientId, selectedSegmentIndex, 'notes', event.target.value);
                        } else {
                          updatePendingAdjustment(selectedSegmentIndex, 'notes', event.target.value);
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {hasPendingDelta && pendingDelta !== 0 ? (
                  <Alert icon={false} severity={pendingDelta >= 0 ? 'success' : 'warning'}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          New effective budget
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }} variant="h6">
                          {formatCurrency(newEffective)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          Change
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }} variant="h6">
                          {pendingDelta > 0 ? '+' : ''}
                          {formatCurrency(pendingDelta)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          From current
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {formatCurrency(currentEffective)} → {formatCurrency(newEffective)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Alert>
                ) : null}
              </Stack>
            );
          })() : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAdjustmentsDialogOpen(false); setMcDialogClientId(null); setAdjustmentMode('total'); }}>Close</Button>
          <Button
            onClick={addAdjustment}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Save Adjustment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" onClose={() => { closeNotesDialog(); setMcDialogClientId(null); }} open={notesDialogOpen}>
        <DialogTitle>Segment Notes</DialogTitle>
        <DialogContent>
          {dialogSegment ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="subtitle2">
                {buildSegmentLabel(dialogSegment, dialogOptionSet)}
              </Typography>
              <TextField
                fullWidth
                minRows={4}
                multiline
                value={dialogSegment.notes || ''}
                onChange={(event) => {
                  if (mcDialogClientId) {
                    multiClient.updateSegmentForClient(mcDialogClientId, selectedSegmentIndex, 'notes', event.target.value);
                  } else {
                    updateSegment(selectedSegmentIndex, 'notes', event.target.value);
                  }
                }}
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { closeNotesDialog(); setMcDialogClientId(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" onClose={() => { closeTurnOffDialog(); setMcDialogClientId(null); }} open={Boolean(turnOffDialogSegment)}>
        <DialogTitle>{isStaff ? 'Confirm Turn Off Segment' : 'Turn Off Segment'}</DialogTitle>
        <DialogContent>
          {turnOffDialogSegment ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="warning">
                {isStaff
                  ? 'Turning off a live segment creates a negative adjustment. It cannot reduce the effective budget below what has already been spent.'
                  : 'This turns the segment off automatically — it takes effect within about 15 minutes, and spend continues until then. The budget can\'t drop below what has already been spent.'}
              </Alert>
              <Typography variant="subtitle2">
                {buildSegmentLabel(turnOffDialogSegment, dialogOptionSet)}
              </Typography>
              <Box>
                <Typography variant="body2">
                  Current effective budget: {formatCurrency(turnOffDialogSegment.effective_amount || turnOffDialogSegment.amount)}
                </Typography>
                <Typography variant="body2">
                  Month-to-date spend: {formatCurrency(turnOffDialogSegment.month_to_date_spend)}
                </Typography>
                <Typography variant="body2">
                  Lowest allowed effective budget after turn off: {formatCurrency(turnOffDialogSegment.month_to_date_spend)}
                </Typography>
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { closeTurnOffDialog(); setMcDialogClientId(null); }}>Cancel</Button>
          <Button
            color="warning"
            onClick={confirmTurnOffSegment}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isStaff ? 'Confirm Turn Off' : 'Turn Off Segment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Undo the whole pending turn-off batch. All-or-nothing: one hold
          window covers every segment in it, so the countdown the client saw
          would stop describing reality if we canceled only part of it. */}
      <Dialog fullWidth maxWidth="xs" onClose={() => setTurnOffCancelOpen(false)} open={turnOffCancelOpen}>
        <DialogTitle>Undo turn-off?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {turnOffBatch?.pending_count === 1
                ? 'This keeps the segment running. Nothing has changed yet — no ads have been stopped.'
                : `This keeps all ${turnOffBatch?.pending_count || 0} segments running. Nothing has changed yet — no ads have been stopped.`}
            </Typography>
            {turnOffSecondsLeft > 0 ? (
              <Typography variant="caption" color="text.secondary">
                Time left to undo: {turnOffCountdownLabel}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTurnOffCancelOpen(false)}>Keep turning off</Button>
          <Button
            color="primary"
            onClick={confirmCancelTurnOff}
            variant="contained"
            disabled={turnOffCanceling}
            startIcon={turnOffCanceling ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Undo turn-off
          </Button>
        </DialogActions>
      </Dialog>

      {/* Market budgets beside an all-territory budget: a soft stop, since a
          few market rows on top of the all row split spend wrong. */}
      <Dialog fullWidth maxWidth="xs" onClose={() => setScopeMixWarning(null)} open={Boolean(scopeMixWarning)}>
        <DialogTitle>Double-check your market budgets</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              This account runs on one budget across all markets. Adding budgets for individual
              markets alongside it changes how spend is split.
            </Alert>
            <Typography color="text.secondary" variant="body2">
              To budget by market, every market needs its own budget, not just a few. To pause a
              market, turn off its ZIP codes on the ZIP Coverage page instead.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScopeMixWarning(null)}>Go Back</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              const next = scopeMixWarning?.onContinue;
              setScopeMixWarning(null);
              if (next) next();
            }}
          >
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Below-spend-floor resolution: a budget under what's already spent
          usually means "stop spending here" — offer to settle at spend and
          continue instead of dead-ending on a validation error. */}
      <Dialog fullWidth maxWidth="sm" onClose={() => setSpendFloorDialog(null)} open={Boolean(spendFloorDialog)}>
        <DialogTitle>Budget is below what&apos;s already spent</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {(spendFloorDialog?.indexes?.length || 0) > 1
                ? 'These markets have already spent more this month than the budgets you entered. A monthly budget can\'t be set below what\'s already been spent.'
                : 'This market has already spent more this month than the budget you entered. A monthly budget can\'t be set below what\'s already been spent.'}
            </Typography>
            {(spendFloorDialog?.indexes || []).map((segmentIndex) => {
              const segment = segments[segmentIndex];
              if (!segment) return null;
              return (
                <Box key={segmentIndex} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'var(--mui-palette-background-level1)' }}>
                  <Typography variant="subtitle2">{buildSegmentLabel(segment, optionSet)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Entered budget: {formatCurrency(segment.amount || 0)} · Spent so far: {formatCurrency(segment.month_to_date_spend)}
                  </Typography>
                </Box>
              );
            })}
            <Alert severity="info">
              &ldquo;Stop Spend&rdquo; settles {(spendFloorDialog?.indexes?.length || 0) > 1 ? 'each budget' : 'the budget'} at
              the amount already spent, so there&apos;s no remaining budget and spend stops for the rest of the month.
              You can raise the budget again any time.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpendFloorDialog(null)}>Go Back &amp; Edit</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={confirmSpendFloorSettle}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Stop Spend &amp; Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post-submit celebration dialog */}
      <Dialog
        open={submitSuccessOpen}
        onClose={() => setSubmitSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, textAlign: 'center', py: 2 } }}
      >
        <DialogContent>
          <Stack spacing={2.5} alignItems="center" sx={{ py: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 36, color: '#fff' }} />
            </Box>
            <Typography variant="h6">Budget Submitted!</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
              Your {formatMonthLabel(period)} budget has been submitted for review.
              Lead Gurus will review it and mark it live before the month starts.
            </Typography>
            <Divider flexItem />
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                What happens next:
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  1. Lead Gurus reviews your segments and amounts
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  2. You&apos;ll get an email when it&apos;s approved and live
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  3. Adjust anytime during the month if your needs change
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button variant="contained" onClick={() => setSubmitSuccessOpen(false)}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        open={snackbar.open}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {walkthroughActive ? (
        <WalkthroughOverlay
          stepIndex={walkthroughStep}
          steps={walkthroughSteps}
          stepRefs={walkthroughRefs}
          onNext={nextWalkthroughStep}
          onBack={prevWalkthroughStep}
          onSkip={endWalkthrough}
        />
      ) : null}
    </Page>
  );
}
